/**
 * DPO Configurator — Storefront Widget
 * Supports all 12 field types, client-side rule engine,
 * real-time pricing, and runtime variant generation.
 * Version: 2.0.0
 */
(function (w, d) {
  "use strict";

  // ── Utilities ─────────────────────────────────────────────────────────────

  function host() {
    return w.CONFIGURATOR_APP_HOST || "/apps/configurator-api";
  }

  function roundUpMoney(n) {
    n = parseFloat(n) || 0;
    return Math.round(n);
  }

  function fmt(n) {
    n = roundUpMoney(n);
    try {
      var currency = (w.Shopify && w.Shopify.currency && w.Shopify.currency.active) ? w.Shopify.currency.active : "USD";
      if (currency.length !== 3) currency = "USD";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(n);
    } catch (_) {
      return "$" + Math.round(n);
    }
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  function el(tag, cls, attrs) {
    var e = d.createElement(tag);
    if (cls) e.className = cls;
    if (attrs) Object.keys(attrs).forEach(function (k) { e.setAttribute(k, attrs[k]); });
    return e;
  }

  function get(obj, path) {
    return path.split(".").reduce(function (o, k) { return o && o[k]; }, obj);
  }

  function setThemePriceLoading() {
    var saleEl = d.getElementById('lwb-sale-price');
    var compareEl = d.getElementById('lwb-compare-price');
    var badgeEl = d.getElementById('lwb-save-badge');
    if (saleEl) saleEl.textContent = "Loading...";
    if (compareEl) compareEl.textContent = "Loading...";
    if (badgeEl) badgeEl.textContent = "Loading...";
  }

  // Sets the Add to Cart button's text/disabled/spinner state. The button's
  // markup always has a .dpo-atc-spinner + .dpo-atc-btn-text child (built in
  // Configurator.prototype._render), so this only ever touches the text span
  // and toggles the loading class rather than clobbering the spinner markup
  // with a raw textContent assignment.
  function setAtcBtnState(btn, text, loading) {
    if (!btn) return;
    btn.classList.toggle("dpo-atc-btn--loading", !!loading);
    btn.disabled = !!loading;
    var textEl = btn.querySelector(".dpo-atc-btn-text");
    if (textEl) {
      textEl.textContent = text;
    } else {
      btn.textContent = text;
    }
  }

  // Cycles a friendly message list in the button while the (potentially
  // slow, ~seconds-long) variant-creation call is in flight, so the wait
  // feels alive instead of stuck on one static line. Returns a stop()
  // function that clears the interval — always call it once the wait ends
  // (success or failure), before setting the final button text.
  function startAtcMessageCycle(btn, messages, intervalMs) {
    var i = 0;
    setAtcBtnState(btn, messages[0], true);
    var timer = setInterval(function () {
      i = (i + 1) % messages.length;
      var textEl = btn && btn.querySelector(".dpo-atc-btn-text");
      if (textEl) textEl.textContent = messages[i];
    }, intervalMs || 2200);
    return function stop() {
      clearInterval(timer);
    };
  }

  // ── Client-side Rule Engine ───────────────────────────────────────────────

  var RuleEngine = {
    match: function (op, selVal, condVal) {
      var s = String(selVal || "").toLowerCase();
      var c = String(condVal || "").toLowerCase();
      switch (op) {
        case "=": return s === c;
        case "!=": return s !== c;
        case ">": return parseFloat(selVal) > parseFloat(condVal);
        case "<": return parseFloat(selVal) < parseFloat(condVal);
        case ">=": return parseFloat(selVal) >= parseFloat(condVal);
        case "<=": return parseFloat(selVal) <= parseFloat(condVal);
        case "contains": return s.indexOf(c) !== -1;
        case "not_contains": return s.indexOf(c) === -1;
        case "is_empty": return s.trim() === "";
        case "is_not_empty": return s.trim() !== "";
        default: return false;
      }
    },

    evaluate: function (conditionJson, visibility, makeIt, selections) {
      // Returns true if field should be visible
      if (!conditionJson) return visibility === "visible";
      var conditions;
      try { conditions = JSON.parse(conditionJson); } catch (_) { return visibility === "visible"; }
      if (!conditions.length) return visibility === "visible";
      var allMatch = conditions.every(function (c) {
        return RuleEngine.match(c.operator, selections[c.conditionFieldId] || "", c.conditionValue || "");
      });
      if (allMatch) return makeIt === "visible" || makeIt === "required";
      return visibility === "visible";
    }
  };

  // ── Client-side Pricing Engine ────────────────────────────────────────────

  var PricingEngine = {
    calculate: function (config, selections, visibleIds) {
      var base = config.basePrice || 0;
      var total = base;
      var breakdown = { "Base Price": base };

      config.fields.forEach(function (field) {
        if (visibleIds.indexOf(field.id) === -1) return;
        var sel = selections[field.id];
        if (sel === undefined || sel === null || sel === "") return;

        // Direct field price (flat fee for any entry)
        if (field.price > 0 && ["input", "textarea", "date", "file_upload"].indexOf(field.type) !== -1) {
          var cost = field.priceType === "percentage"
            ? base * field.price / 100
            : field.price;
          total += cost;
          breakdown[field.title] = (breakdown[field.title] || 0) + cost;
        }

        // Value-level modifiers for choice fields
        if (["dropdown", "radio", "checkbox", "color_swatch", "image_swatch"].indexOf(field.type) !== -1) {
          var chosen = Array.isArray(sel) ? sel : [sel];
          chosen.forEach(function (v) {
            var matched = null;
            for (var i = 0; i < (field.values || []).length; i++) {
              if (field.values[i].value === v) {
                matched = field.values[i];
                break;
              }
            }
            if (matched && matched.priceModifier !== 0) {
              var mod = matched.priceType === "percentage"
                ? base * matched.priceModifier / 100
                : matched.priceModifier;
              total += mod;
              breakdown[field.title + ": " + matched.label] = (breakdown[field.title + ": " + matched.label] || 0) + mod;
            }
          });
        }

        // Per-character engraving (text fields with price = 0)
        if (["input", "textarea"].indexOf(field.type) !== -1 && field.price === 0 && config.engravingRate > 0) {
          var txt = String(sel);
          if (txt.length > 0) {
            var engrCost = txt.length * config.engravingRate;
            total += engrCost;
            breakdown[field.title + " (" + txt.length + " chars)"] = engrCost;
          }
        }

        // Range / number multiplier
        if (["range_slider", "step_counter", "number"].indexOf(field.type) !== -1 && field.price > 0) {
          var num = parseFloat(sel) || 0;
          var numCost = field.priceType === "percentage"
            ? base * field.price * num / 100
            : num * field.price;
          total += numCost;
          breakdown[field.title + " × " + num] = numCost;
        }
      });

      return { finalPrice: Math.max(0, Math.round(total * 100) / 100), breakdown: breakdown };
    }
  };

  // ── Configurator Class ────────────────────────────────────────────────────

  function Configurator(container) {
    this.container = container;
    this.config = null;
    this.sections = [];
    this.selections = {};
    this.price = 0;
    this.priceReady = false;
    this._priceRequestId = 0;
    this.currentVariantPromise = null;
    this.currentSelectionsConfig = ""; // Track the exact selections used for the promise
    this._calcDebounced = debounce(this._updatePrice.bind(this), 350);
    this._init();
  }

  Configurator.prototype._init = function () {
    var self = this;
    if (this.container.dataset.rendered === "true") return;
    this.container.dataset.rendered = "true";

    var productId = this.container.dataset.productId || w.CONFIGURATOR_PRODUCT_ID || "";
    if (!productId) { console.error("[DPO] No productId"); return; }

    this.container.innerHTML = '<div class="dpo-loading"><div class="dpo-spinner"></div><span>Loading options...</span></div>';

    // ZERO-LATENCY METAFIELD CACHE LOAD
    if (w.DPO_PRELOADED_CONFIG) {
      if (!w.DPO_PRELOADED_CONFIG.fields || w.DPO_PRELOADED_CONFIG.fields.length === 0) {
        var style = d.getElementById("dpo-hide-native-style");
        if (style) style.remove();
        self.container.innerHTML = '<div class="dpo-empty" style="display:none;">No product options configured.</div>';
        return;
      }
      self.config = w.DPO_PRELOADED_CONFIG;
      // Map the payload to a dummy section to mirror the API data structure perfectly
      self.sections = [{ id: "custom_dpo", label: "Custom Configuration", fields: w.DPO_PRELOADED_CONFIG.fields }];
      self.price = w.DPO_PRELOADED_CONFIG.basePrice || 0;
      self._applyDefaults();
      self._injectEngravingFields();
      self._render();
      return;
    }

    // FALLBACK: Async API Load
    fetch(host() + "/public-api/configurator?productId=" + encodeURIComponent(productId))
      .then(function (r) { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); })
      .then(function (cfg) {
        if (!cfg || cfg.error) {
          var style = d.getElementById("dpo-hide-native-style");
          if (style) style.remove();
          self.container.innerHTML = '<div class="dpo-empty" style="display:none;">No product options configured.</div>';
          return;
        }
        self.config = cfg;
        self.sections = cfg.sections || [];
        self.price = cfg.basePrice || 0;
        self._applyDefaults();
        self._injectEngravingFields();
        self._render();
      })
      .catch(function (err) {
        console.error("[DPO] Load error:", err);
        var style = d.getElementById("dpo-hide-native-style");
        if (style) style.remove();
        self.container.innerHTML = '<div class="dpo-error" style="display:none;">Could not load product options.</div>';
      });
  };

  // Apply default values from config, overridden by URL query parameters if present
  Configurator.prototype._applyDefaults = function () {
    var self = this;
    var choiceTypes = ["dropdown", "radio", "checkbox", "color_swatch", "image_swatch"];

    function normalizeKey(str) {
      return (str || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    }

    function matchChoice(choice, paramValue) {
      if (!choice || !paramValue) return false;
      var cLabel = (choice.label || '').toLowerCase().trim();
      var cVal = String(choice.value || '').toLowerCase().trim();
      var pVal = String(paramValue).toLowerCase().trim();
      if (cLabel === pVal || cVal === pVal) return true;
      
      var normLabel = normalizeKey(cLabel);
      var normVal = normalizeKey(cVal);
      var normParam = normalizeKey(pVal);
      if (normLabel === normParam || normVal === normParam) return true;
      
      // Loose matching: e.g. "rose-gold" matches "14k-rose-gold"
      if (normParam.length > 2 && (normLabel.indexOf(normParam) !== -1 || normParam.indexOf(normLabel) !== -1)) return true;
      if (normParam.length > 2 && (normVal.indexOf(normParam) !== -1 || normParam.indexOf(normVal) !== -1)) return true;
      
      return false;
    }

    var urlParams = new URLSearchParams(w.location.search);

    (this.sections || []).forEach(function (sec) {
      (sec.fields || []).forEach(function (field) {
        var hasUrlParam = false;
        var normTitle = normalizeKey(field.title || field.name || '');

        if (normTitle && urlParams.has(normTitle)) {
          var paramVal = urlParams.get(normTitle);
          if (field.values && field.values.length > 0) {
            var matchedChoice = field.values.find(function (v) {
              return matchChoice(v, paramVal);
            });
            if (matchedChoice) {
              self.selections[field.id] = matchedChoice.value;
              hasUrlParam = true;
            }
          } else {
            self.selections[field.id] = paramVal;
            hasUrlParam = true;
          }
        }

        if (!hasUrlParam) {
          // Explicit defaultValue wins
          if (field.defaultValue !== null && field.defaultValue !== undefined && field.defaultValue !== "") {
            self.selections[field.id] = field.defaultValue;
          }
          // isDefault marker on a value wins
          (field.values || []).forEach(function (val) {
            if (val.isDefault) self.selections[field.id] = val.value;
          });
          // For choice fields with no selection yet, auto-select the first value.
          if (choiceTypes.indexOf(field.type) !== -1 &&
            (self.selections[field.id] === undefined || self.selections[field.id] === null || self.selections[field.id] === "") &&
            field.values && field.values.length > 0) {
            self.selections[field.id] = field.values[0].value;
          }
          // Step counters
          if (field.type === 'step_counter' &&
            (self.selections[field.id] === undefined || self.selections[field.id] === null || self.selections[field.id] === "")) {
            self.selections[field.id] = parseInt(field.defaultValue) || 1;
          }
          // Range sliders
          if (field.type === 'range_slider' &&
            (self.selections[field.id] === undefined || self.selections[field.id] === null || self.selections[field.id] === "")) {
            self.selections[field.id] = parseFloat(field.defaultValue) || parseFloat(field.min) || 0;
          }
        }
      });
    });
  };

  // Returns true if this product is a plain/classic ring (should show Width field).
  // Tag-based detection is the primary signal — add "plain-ring" or "classic-ring" tag
  // in Shopify admin to opt a product in. Collection and DOM signals are fallbacks.
  Configurator.prototype._isPlainRingProduct = function (fields) {
    // Signal 1: Shopify product tags (set from Liquid via DPO_PRODUCT_TAGS)
    var tags = w.DPO_PRODUCT_TAGS || [];
    var plainTags = ['plain-ring', 'classic-ring', 'plain-band', 'classic-band', 'celtic-ring', 'celtic-band', 'celtic'];
    for (var ti = 0; ti < tags.length; ti++) {
      var tagLc = (tags[ti] || '').toLowerCase();
      for (var pt = 0; pt < plainTags.length; pt++) {
        if (tagLc === plainTags[pt]) return true;
      }
    }
    // Signal 2: Shopify collection handles (set from Liquid via DPO_PRODUCT_COLLECTIONS)
    var productCols = w.DPO_PRODUCT_COLLECTIONS || [];
    var plainCols = ['plain-rings', 'plain-bands', 'classic-rings', 'classic-bands', 'celtic-rings', 'celtic-bands', 'celtic'];
    for (var pi = 0; pi < productCols.length; pi++) {
      for (var li = 0; li < plainCols.length; li++) {
        if (productCols[pi] === plainCols[li]) return true;
      }
    }
    // Signal 3: breadcrumb DOM contains "plain" or "classic" (works without Liquid globals)
    var crumbs = d.querySelectorAll('.lwb-breadcrumb__item');
    for (var bi = 0; bi < crumbs.length; bi++) {
      var crumbText = (crumbs[bi].textContent || '').toLowerCase();
      if (crumbText.indexOf('plain') !== -1 || crumbText.indexOf('classic') !== -1 || crumbText.indexOf('celtic') !== -1) return true;
    }
    // Signal 4: Width field with multiple values already present in the DPO config
    for (var fi = 0; fi < fields.length; fi++) {
      var f = fields[fi];
      if (!f) continue;
      var titleLc = (f.title || '').toLowerCase();
      if (titleLc === 'width' && (f.values || []).length > 1) return true;
    }
    return false;
  };

  // Inject engraving text fields:
  //   - After "His Size" → "His Engraving"  (couples rings)
  //   - After "Her Size" → "Her Engraving"  (couples rings)
  //   - After plain "Size" → "Engraving"    (plain single rings only)
  // Plain-ring detection: Width field present in config OR product in a plain-ring collection.
  Configurator.prototype._injectEngravingFields = function () {
    try {
      function makeField(id, title) {
        return {
          id: id,
          title: title,
          type: "input",
          values: [],
          conditionJson: null,
          visibility: "visible",
          makeIt: "visible",
          required: false,
          maxLength: 50,
          price: 0,
          priceType: "flat",
          hideSkuInCart: false,
          cssClass: ""
        };
      }

      // Width radio field injected before "Finishing" for plain rings (2 mm – 12 mm).
      // Price modifiers are all 0 here; width-based pricing is handled by the DPO
      // variant-matching engine (the chosen width selects the correct Shopify variant).
      function makeWidthField() {
        var widths = ['2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
        return {
          id: 'lwb_width',
          title: 'Width',
          type: 'radio',
          values: widths.map(function (w) {
            return { value: w, label: w + 'mm', priceModifier: 0, priceType: 'flat', isDefault: w === '2' };
          }),
          conditionJson: null,
          visibility: 'visible',
          makeIt: 'visible',
          required: false,
          price: 0,
          priceType: 'flat',
          hideSkuInCart: false,
          cssClass: ''
        };
      }

      // Pairs: field title substring → engraving field definition
      var ENGRAVING_MAP = [
        { match: "his size", inject: makeField("lwb_his_engraving", "His Engraving") },
        { match: "her size", inject: makeField("lwb_her_engraving", "Her Engraving") }
      ];

      var sections = this.sections || [];
      for (var s = 0; s < sections.length; s++) {
        var sec = sections[s];
        // Work on a COPY so the original preloaded config is never mutated
        var fields = Array.prototype.slice.call(sec.fields || []);
        var inserts = []; // [{ afterIdx, field }]

        // Pre-scan: determine if this product needs plain-ring fields
        var isPlain = this._isPlainRingProduct(fields);
        if (isPlain) this._isPlainRing = true; // persist for _render / _applyVisibility

        // Pre-scan: check if Width/Engraving already exist in the DPO config
        var hasWidthField = false;
        var hasEngravingField = false;
        for (var px = 0; px < fields.length; px++) {
          if (!fields[px]) continue;
          var ptitle = (fields[px].title || '').toLowerCase();
          if (ptitle.indexOf('width') !== -1) hasWidthField = true;
          if (ptitle === 'engraving' || fields[px].id === 'lwb_engraving') hasEngravingField = true;
        }

        for (var i = 0; i < fields.length; i++) {
          var field = fields[i];
          if (!field) continue;
          var t = (field.title || "").toLowerCase();

          // His / Her engraving injection (couples rings)
          for (var r = 0; r < ENGRAVING_MAP.length; r++) {
            var rule = ENGRAVING_MAP[r];
            if (t.indexOf(rule.match) === -1) continue;

            // Guard: only inject once per engraving ID
            var alreadyInjected = false;
            for (var j = 0; j < fields.length; j++) {
              if (fields[j] && fields[j].id === rule.inject.id) {
                alreadyInjected = true;
                break;
              }
            }
            if (!alreadyInjected) {
              inserts.push({ afterIdx: i, field: rule.inject });
            }
          }

          // Plain-ring Width injection: before the "Finishing" field
          if (isPlain && !hasWidthField && t.indexOf('finishing') !== -1) {
            var widthAlreadyQueued = false;
            for (var wi = 0; wi < inserts.length; wi++) {
              if (inserts[wi].field.id === 'lwb_width') { widthAlreadyQueued = true; break; }
            }
            if (!widthAlreadyQueued) {
              // beforeIdx: inject BEFORE field i — simulate by inserting after i-1
              inserts.push({ afterIdx: i - 1, field: makeWidthField() });
            }
          }

          // Plain-ring Engraving injection: after the plain "Size" field
          if (isPlain && !hasEngravingField &&
              t.indexOf('size') !== -1 && t.indexOf('his') === -1 && t.indexOf('her') === -1) {
            var engravingAlreadyQueued = false;
            for (var ek = 0; ek < inserts.length; ek++) {
              if (inserts[ek].field.id === 'lwb_engraving') { engravingAlreadyQueued = true; break; }
            }
            if (!engravingAlreadyQueued) {
              inserts.push({ afterIdx: i, field: makeField('lwb_engraving', 'Engraving') });
            }
          }
        }

        // Splice from the end so earlier indices remain valid
        inserts.sort(function (a, b) { return b.afterIdx - a.afterIdx; });
        for (var k = 0; k < inserts.length; k++) {
          var afterIdx = Math.max(inserts[k].afterIdx, 0);
          fields.splice(afterIdx + 1, 0, inserts[k].field);
        }
        sec.fields = fields;
      }
    } catch (e) {
      console.warn('[DPO] _injectEngravingFields error:', e);
    }
  };

  // Compute which fields should be visible
  Configurator.prototype._visibleIds = function () {
    var self = this;
    var visible = [];
    (this.sections || []).forEach(function (sec) {
      (sec.fields || []).forEach(function (field) {
        if (RuleEngine.evaluate(field.conditionJson, field.visibility, field.makeIt, self.selections)) {
          visible.push(field.id);
        }
      });
    });
    return visible;
  };

  // Returns selections filtered for API use — strips locally-injected fields (lwb_*)
  // that the backend /checkout endpoint doesn't know about and would fail on.
  Configurator.prototype._getCheckoutSelections = function () {
    var filtered = {};
    var sels = this.selections || {};
    Object.keys(sels).forEach(function (k) {
      if (k.indexOf('lwb_') === 0) return; // skip injected local fields
      filtered[k] = sels[k];
    });
    return filtered;
  };

  Configurator.prototype._getVariantSelections = function () {
    var self = this;
    var variantSelections = {};
    var variantFieldTypes = {
      dropdown: true,
      radio: true,
      checkbox: true,
      color_swatch: true,
      image_swatch: true,
      range_slider: true,
      step_counter: true
    };

    (this.sections || []).forEach(function (sec) {
      (sec.fields || []).forEach(function (field) {
        if (!field || !variantFieldTypes[field.type]) return;
        var value = self.selections[field.id];
        if (value === undefined || value === null || value === "") return;
        variantSelections[field.id] = value;
      });
    });

    return variantSelections;
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  Configurator.prototype._render = function () {
    var self = this;
    var wrap = el("div", "dpo-wrap");

    // For each section
    (this.sections || []).forEach(function (sec) {
      var secEl = el("div", "dpo-section");
      secEl.dataset.sectionId = sec.id;

      if (sec.label) {
        var lbl = el("div", "dpo-section-label");
        lbl.textContent = sec.label;
        secEl.appendChild(lbl);
      }

      var grid = el("div", "dpo-fields-grid");
      grid.style.setProperty('--dpo-grid-cols', sec.columns || 3);

      (sec.fields || []).forEach(function (field) {
        var fieldEl = self._renderField(field);
        fieldEl.dataset.fieldId = field.id;
        // Initial visibility — only hide if: condition says hidden AND the field
        // has no current selection (no value to contribute to price/UX).
        // Fields that already have a selection (from defaults or auto-first-value)
        // are always shown so users can see and change their selection.
        // Width fields with values are always shown for plain-ring products.
        var shouldShow = RuleEngine.evaluate(field.conditionJson, field.visibility, field.makeIt, self.selections);
        var hasSelection = self.selections[field.id] !== undefined && self.selections[field.id] !== null && self.selections[field.id] !== "";
        var ftitle = (field.title || '').toLowerCase();
        var isWidthWithValues = ftitle.indexOf('width') !== -1 && (field.values || []).length > 0;
        var isPlainSwatchField = self._isPlainRing && (ftitle.indexOf('finishing') !== -1 || ftitle.indexOf('thickness') !== -1);
        var isThicknessField = ftitle.indexOf('thickness') !== -1;
        if (isThicknessField && !self._isPlainRing) {
          fieldEl.style.display = "none";
        } else if (!shouldShow && !hasSelection && !isWidthWithValues && !isPlainSwatchField) {
          fieldEl.style.display = "none";
        }
        grid.appendChild(fieldEl);
      });

      secEl.appendChild(grid);
      wrap.appendChild(secEl);

      // Divider
      var hr = el("div", "dpo-divider");
      wrap.appendChild(hr);
    });

    // Price display — show the known base price immediately (from the
    // preloaded metafield config, zero network latency) instead of a blank
    // "Loading..." placeholder. _updatePrice() overwrites this once the
    // real calculate-price API call resolves.
    var priceSection = el("div", "dpo-price-wrap");
    priceSection.innerHTML =
      '<div class="dpo-price" id="dpo-price-' + this.config.id + '">' + fmt(this.price || this.config.basePrice || 0) + '</div>' +
      '<div class="dpo-breakdown" id="dpo-breakdown-' + this.config.id + '"></div>';
    wrap.appendChild(priceSection);

    // Add to Cart button — stays clickable from the start, showing the base
    // price. _handleAddToCart() already guards on this.priceReady and shows
    // a friendly "please wait" message rather than submitting a stale price
    // if clicked before the real price/variant have been confirmed, so there
    // is no correctness risk in leaving this enabled instead of disabled.
    var atcBtn = el("button", "dpo-atc-btn", { id: "dpo-atc-btn-" + this.config.id, type: "button" });
    atcBtn.innerHTML = '<span class="dpo-atc-spinner" aria-hidden="true"></span><span class="dpo-atc-btn-text"></span>';
    setAtcBtnState(atcBtn, "Add to Cart - " + fmt(this.price || this.config.basePrice || 0), false);
    atcBtn.addEventListener("click", this._handleAddToCart.bind(this));
    wrap.appendChild(atcBtn);

    // Error display
    var errEl = el("div", "dpo-error-msg");
    errEl.id = "dpo-err-" + this.config.id;
    errEl.style.display = "none";
    wrap.appendChild(errEl);

    // Inject custom CSS from settings
    if (this.config.customCss) {
      var styleEl = el("style");
      styleEl.textContent = this.config.customCss;
      wrap.appendChild(styleEl);
    }

    this.container.innerHTML = "";
    this.container.appendChild(wrap);
    setThemePriceLoading();

    // Hide native variant selectors (we replace them with our own UI)
    var nativeForm = d.querySelector('form[action^="/cart/add"]');
    if (nativeForm) {
      nativeForm.querySelectorAll(
        'variant-radios, variant-selects, .variant-wrapper, .product-form__input, .selector-wrapper'
      ).forEach(function (el) { el.style.display = 'none'; });

      // Hide the native submit button (our ATC button handles checkout)
      var nativeBtn = nativeForm.querySelector(
        '[type="submit"], [name="add"], button.product-form__submit, button.add-to-cart'
      );
      if (nativeBtn) nativeBtn.style.display = 'none';
    }

    // Hide "Buy it now" — it bypasses our configurator
    d.querySelectorAll('.shopify-payment-button, [data-shopify-payment-button]')
      .forEach(function (btn) { btn.style.display = 'none'; });

    this._updatePrice();
    this._applyVisibility(); // re-sync visibility now that DOM exists and defaults are seeded
    this._markVariantImages();

    // Sync gallery images based on initial selections (either from URL parameters or defaults)
    (this.sections || []).forEach(function (sec) {
      (sec.fields || []).forEach(function (field) {
        var val = self.selections[field.id];
        if (val !== undefined && val !== null && val !== "") {
          self._syncGalleryFromSelections(field.id, val);
        }
      });
    });

    this._updateGridLayout();
    if (!this._resizeHandlerAdded) {
      window.addEventListener('resize', this._updateGridLayout.bind(this));
      this._resizeHandlerAdded = true;
    }
  };

  // Hide all gallery images that don't have alt text "default products image".
  // Reads alt text directly from the DOM img elements — no DPO_PRODUCT_MEDIA needed.
  Configurator.prototype._markVariantImages = function () {
    console.log('[DPO Gallery] _markVariantImages running...');

    var viewer = d.querySelector('[id^="GalleryViewer-"]');
    var thumbContainer = d.querySelector('[id^="GalleryThumbnails-"]');

    if (!viewer) {
      console.warn('[DPO Gallery] No GalleryViewer found — skipping');
      return;
    }

    // ── Hide non-default slides in the main viewer ──────────────────────────
    var slides = viewer.querySelectorAll('[data-media-id]');
    console.log('[DPO Gallery] Main viewer slides found:', slides.length);

    [].forEach.call(slides, function (slide) {
      var img = slide.querySelector('img');
      var alt = img ? (img.getAttribute('alt') || '').toLowerCase().trim() : '';
      var mediaId = slide.getAttribute('data-media-id');
      var isDefault = alt === 'default products image';
      console.log('[DPO Gallery] Slide', mediaId, '| alt="' + alt + '" | isDefault:', isDefault);
      if (!isDefault) {
        slide.classList.add('dpo-variant-image');
      }
    });

    // ── Hide non-default items in the thumbnail rail ────────────────────────
    if (!thumbContainer) {
      console.warn('[DPO Gallery] No GalleryThumbnails found — thumbnail hiding skipped');
      return;
    }

    var thumbItems = thumbContainer.querySelectorAll('.thumbnail-list__item');
    console.log('[DPO Gallery] Thumbnail items found:', thumbItems.length);

    [].forEach.call(thumbItems, function (li) {
      var img = li.querySelector('img');
      var alt = img ? (img.getAttribute('alt') || '').toLowerCase().trim() : '';
      var target = li.getAttribute('data-target');
      var isDefault = alt === 'default products image';
      console.log('[DPO Gallery] Thumb', target, '| alt="' + alt + '" | isDefault:', isDefault);
      if (!isDefault) {
        li.classList.add('dpo-variant-thumb');
      }
    });

    console.log('[DPO Gallery] Done. Slides hidden:', viewer.querySelectorAll('.dpo-variant-image').length,
      '| Thumbs hidden:', thumbContainer.querySelectorAll('.dpo-variant-thumb').length);
  };

  // ── Field Renderers ────────────────────────────────────────────────────────

  Configurator.prototype._renderField = function (field) {
    var wrap = el("div", "dpo-field");
    var titleLc = (field.title || "").toLowerCase();
    if (field.cssClass) wrap.classList.add(field.cssClass);
    if (titleLc === "color") wrap.classList.add("dpo-field--color");
    if (titleLc === "material") wrap.classList.add("dpo-field--material");
    if (titleLc.indexOf("width") !== -1) wrap.classList.add("dpo-field--width");

    if (
      titleLc.indexOf("size") !== -1 ||
      field.type === "input" ||
      field.type === "textarea" ||
      field.type === "number" ||
      field.type === "date"
    ) {
      wrap.classList.add("dpo-field--wide");
    }

    // Only hide choice fields that have zero values (nothing to render at all).
    var choiceTypes = ["radio", "image_swatch", "color_swatch", "checkbox", "dropdown"];
    if (choiceTypes.indexOf(field.type) !== -1 && (field.values || []).length === 0) {
      wrap.style.display = "none";
      wrap.dataset.dpoLocked = "true";
      return wrap;
    }

    // Hide radio/dropdown fields that have only one value — no choice to make.
    if (["radio", "dropdown"].indexOf(field.type) !== -1 && (field.values || []).length === 1) {
      wrap.style.display = "none";
      wrap.dataset.dpoLocked = "true";
      return wrap;
    }

    // Label + Selection Display
    var lblContainer = el("div", "dpo-field-header");
    var lbl = el("label", "dpo-field-label");
    lbl.textContent = field.title + ":";

    var valDisplay = el("span", "dpo-selected-val", { id: "dpo-val-" + field.id });
    // Find initial default label
    var initial = this.selections[field.id];
    var matched = (field.values || []).find(v => v.value === initial);
    valDisplay.textContent = matched ? " " + matched.label : "";

    lblContainer.appendChild(lbl);
    lblContainer.appendChild(valDisplay);

    if (titleLc.indexOf("size") !== -1) {
      var guide = el("a", "dpo-size-guide");
      guide.href = "https://loveweddingbands.com/pages/ring-size-guide";
      guide.target = "_blank";
      guide.textContent = "Size Guide";
      lblContainer.appendChild(guide);
    }

    wrap.appendChild(lblContainer);

    // Input
    var input = this._buildInput(field);
    if (input) wrap.appendChild(input);

    return wrap;
  };

  Configurator.prototype._openSizeGuide = function () {
    var modal = d.getElementById('dpo-size-modal');
    if (!modal) {
      modal = d.createElement('div');
      modal.id = 'dpo-size-modal';

      /* ── Overlay backdrop ── */
      var ms = modal.style;
      ms.position   = 'fixed';
      ms.inset      = '0';
      ms.zIndex     = '99999';
      ms.background = 'rgba(0,0,0,0.6)';
      ms.display    = 'none';
      ms.alignItems = 'center';
      ms.justifyContent = 'center';
      ms.padding    = '20px';
      ms.boxSizing  = 'border-box';

      /* ── White content box ── */
      var content = d.createElement('div');
      var cs = content.style;
      cs.position      = 'relative';
      cs.background    = '#fff';
      cs.maxWidth      = '820px';
      cs.width         = '100%';
      cs.padding       = '28px 24px 24px';
      cs.boxSizing     = 'border-box';
      cs.maxHeight     = '90vh';
      cs.display       = 'flex';
      cs.flexDirection = 'column';
      cs.boxShadow     = '0 8px 40px rgba(0,0,0,0.28)';

      /* ── Close button ── */
      var closeBtn = d.createElement('button');
      var cbs = closeBtn.style;
      cbs.position   = 'absolute';
      cbs.top        = '12px';
      cbs.right      = '16px';
      cbs.background = 'none';
      cbs.border     = 'none';
      cbs.fontSize   = '26px';
      cbs.lineHeight = '1';
      cbs.cursor     = 'pointer';
      cbs.color      = '#333';
      closeBtn.innerHTML = '&times;';
      closeBtn.setAttribute('aria-label', 'Close');
      closeBtn.onclick = function () {
        modal.style.display = 'none';
        document.body.style.overflow = '';
      };

      /* Close on backdrop click */
      modal.addEventListener('click', function (e) {
        if (e.target === modal) {
          modal.style.display = 'none';
          document.body.style.overflow = '';
        }
      });

      /* Close on Escape key */
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && modal.style.display === 'flex') {
          modal.style.display = 'none';
          document.body.style.overflow = '';
        }
      });

      /* ── Title ── */
      var title = d.createElement('h3');
      title.textContent = 'Ring Size Guide';
      title.style.margin      = '0 0 14px';
      title.style.textAlign   = 'center';
      title.style.fontSize    = '18px';
      title.style.fontWeight  = '600';
      title.style.color       = '#1a1a1a';
      title.style.fontFamily  = 'inherit';

      /* ── PDF iframe ── */
      var iframe = d.createElement('iframe');
      iframe.src = 'https://cdn.shopify.com/s/files/1/0712/8589/9324/files/ring_sizer_loveweddinbands_1.pdf?v=1779859759#toolbar=0&navpanes=0&scrollbar=0&view=FitH';
      iframe.style.width   = '100%';
      iframe.style.flex    = '1';
      iframe.style.minHeight = '65vh';
      iframe.style.border  = 'none';
      iframe.style.display = 'block';
      iframe.setAttribute('title', 'Ring Size Guide PDF');

      content.appendChild(closeBtn);
      content.appendChild(title);
      content.appendChild(iframe);
      modal.appendChild(content);
      d.body.appendChild(modal);
    }

    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  };

  Configurator.prototype._buildInput = function (field) {
    var self = this;
    var type = field.type;

    if (type === "input") return this._mkTextInput(field);
    if (type === "textarea") return this._mkTextarea(field);
    if (type === "number") return this._mkNumber(field);
    if (type === "date") return this._mkDate(field);
    if (type === "dropdown") return this._mkDropdown(field);
    if (type === "radio") return this._mkRadio(field);
    if (type === "checkbox") return this._mkCheckbox(field);
    if (type === "color_swatch") return this._mkColorSwatch(field);
    if (type === "image_swatch") return this._mkImageSwatch(field);
    if (type === "file_upload") return this._mkFileUpload(field);
    if (type === "range_slider") return this._mkRangeSlider(field);
    if (type === "step_counter") return this._mkStepCounter(field);
    return null;
  };

  Configurator.prototype._updateURLQuery = function () {
    var self = this;
    var url = new URL(w.location.href);

    function normalizeKey(str) {
      return (str || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    }

    (this.sections || []).forEach(function (sec) {
      (sec.fields || []).forEach(function (field) {
        var normTitle = normalizeKey(field.title || field.name || '');
        if (!normTitle) return;

        var val = self.selections[field.id];
        if (val !== undefined && val !== null && val !== "") {
          var label = null;
          if (field.values && field.values.length > 0) {
            var matched = field.values.find(function (v) { return v.value === val; });
            if (matched) label = matched.label;
          }
          var paramVal = label || val;
          url.searchParams.set(normTitle, paramVal);
        } else {
          url.searchParams.delete(normTitle);
        }
      });
    });

    w.history.replaceState({}, '', url.pathname + url.search + url.hash);
  };

  Configurator.prototype._onChange = function (fieldId, value) {
    this.selections[fieldId] = value;

    // Update Label Display Value
    var valEl = d.getElementById("dpo-val-" + fieldId);
    if (valEl) {
      // Find field config to get the label of the selected value
      var field = null;
      for (var i = 0; i < this.sections.length; i++) {
        field = (this.sections[i].fields || []).find(f => f.id === fieldId);
        if (field) break;
      }
      if (field) {
        var matched = (field.values || []).find(v => v.value === value);
        valEl.textContent = matched ? " " + matched.label : " " + value;
      }
    }

    this._syncGalleryFromSelections(fieldId, value);
    this._applyVisibility();
    this._calcDebounced();
    this._updateGridLayout();
    this._updateURLQuery();
  };

  // ── Instant Gallery Sync ──────────────────────────────────────────────────
  // Matches the selected value's label (or raw value) against product image
  // alt texts and switches the gallery immediately — zero API wait.
  //
  // How to use in Shopify: Set each product image's alt text to the option
  // value it represents, e.g. "Rose Gold", "White Gold", "Yellow Gold".
  // The match is case-insensitive and trims whitespace.
  //
  // Normalize a Shopify CDN URL: strip query params, size suffixes, protocol
  Configurator.prototype._normUrl = function (url) {
    return (url || '')
      .split('?')[0]
      .replace(/_\d+x\d*(_crop_\w+)?\.(jpg|jpeg|png|gif|webp)$/i, '.$2')
      .replace(/^https?:/, '');
  };

  Configurator.prototype._syncGalleryFromSelections = function (changedFieldId, changedValue) {
    var self = this;

    // Find the selected choice object for this field+value
    var selectedChoice = null;
    var changedFieldTitle = '';
    for (var s = 0; s < this.sections.length; s++) {
      var fields = this.sections[s].fields || [];
      for (var f = 0; f < fields.length; f++) {
        if (fields[f].id === changedFieldId) {
          changedFieldTitle = (fields[f].title || '').toLowerCase().trim();
          var vals = fields[f].values || [];
          for (var v = 0; v < vals.length; v++) {
            if (vals[v].value === changedValue) { selectedChoice = vals[v]; break; }
          }
          break;
        }
      }
      if (selectedChoice) break;
    }

    console.group('[DPO DEBUG] Gallery Update for field:', changedFieldId, 'value:', changedValue);
    console.log('[DPO DEBUG] Selected Choice Object:', selectedChoice);

    // ── Strategy 0: Combined multi-label alt-text search ─────────────────────
    // When multiple fields are active (e.g. Yellow metal + Brush finishing),
    // find the image whose alt text matches the MOST active selection labels.
    // A score ≥ 2 beats Strategy 1's single-option explicit mediaId so that
    // "yellow brush" wins over "white brush" when yellow is also selected.
    (function () {
      var dpoAll = w.DPO_PRODUCT_MEDIA || [];
      if (!dpoAll.length) return;

      // Build label list: changed field first, then all other active fields
      var activeLabels = [];
      var changedLabelS0 = selectedChoice ? selectedChoice.label : changedValue;
      if (changedLabelS0) activeLabels.push(String(changedLabelS0).toLowerCase().trim());

      for (var si = 0; si < (self.sections || []).length; si++) {
        var flds = self.sections[si].fields || [];
        for (var fi = 0; fi < flds.length; fi++) {
          var fObj = flds[fi];
          if (fObj.id === changedFieldId) continue;
          var sv = self.selections[fObj.id];
          if (!sv) continue;
          var fVals = fObj.values || [];
          for (var fvi = 0; fvi < fVals.length; fvi++) {
            if (fVals[fvi].value === sv) {
              activeLabels.push(fVals[fvi].label.toLowerCase().trim());
              break;
            }
          }
        }
      }

      if (activeLabels.length < 2) return; // single selection — let Strategy 1 handle it

      var bestMedia = null;
      var bestScore = 0;
      var bestAltLength = 9999;
      for (var mi = 0; mi < dpoAll.length; mi++) {
        var alt0 = (dpoAll[mi].alt || '').toLowerCase().trim();
        if (!alt0 || alt0 === 'default products image') continue;
        var score = 0;
        for (var li = 0; li < activeLabels.length; li++) {
          if (alt0.indexOf(activeLabels[li]) !== -1) score++;
        }
        // Must contain the changed field's label AND at least one other
        var hasChanged = alt0.indexOf(activeLabels[0]) !== -1;
        if (hasChanged) {
          if (score > bestScore) {
            bestScore = score;
            bestMedia = dpoAll[mi];
            bestAltLength = alt0.length;
          } else if (score === bestScore && alt0.length < bestAltLength) {
            bestMedia = dpoAll[mi];
            bestAltLength = alt0.length;
          }
        }
      }

      if (bestMedia && bestScore >= 2) {
        console.log('[DPO DEBUG] Strategy 0: Combined match! Alt="' + bestMedia.alt + '" score=' + bestScore + '/' + activeLabels.length + ' labels:', activeLabels);
        self._setGalleryMedia(bestMedia.id);
        console.groupEnd();
        return; // exits the IIFE — outer function continues but we flag done
      }
    })();

    // Check if Strategy 0 already set a specific image — if so, skip the rest.
    // We detect this by testing whether the currently active slide's alt is now
    // non-default (Strategy 0 would have just called _setGalleryMedia).
    // This lightweight check avoids adding a shared flag variable.
    var _s0done = false;
    (function () {
      var activeSlide0 = d.querySelector('[id^="GalleryViewer-"] .is-active[data-media-id]');
      if (!activeSlide0) return;
      var activeKey0 = activeSlide0.getAttribute('data-media-id');
      var activeNumId0 = activeKey0.split('-').pop();
      var dpoArr0 = w.DPO_PRODUCT_MEDIA || [];
      for (var gi = 0; gi < dpoArr0.length; gi++) {
        if (String(dpoArr0[gi].id) === activeNumId0) {
          var a = (dpoArr0[gi].alt || '').toLowerCase().trim();
          if (a && a !== 'default products image') {
            var changedLow = (selectedChoice ? selectedChoice.label : changedValue || '').toString().toLowerCase().trim();
            if (a.indexOf(changedLow) !== -1) _s0done = true;
          }
          break;
        }
      }
    })();
    if (_s0done) { console.groupEnd(); return; }

    if (selectedChoice && selectedChoice.mediaId) {
      console.log('[DPO DEBUG] Strategy 1: Found explicit mediaId:', selectedChoice.mediaId);
      this._setGalleryMedia(selectedChoice.mediaId);
      console.groupEnd();
      return;
    }

    var changedLabel = selectedChoice ? selectedChoice.label : null;
    var candidates = [];
    if (changedLabel) candidates.push(changedLabel.toLowerCase().trim());
    if (changedValue && typeof changedValue === 'string' && changedValue !== changedLabel) {
      candidates.push(changedValue.toLowerCase().trim());
    }

    if (changedFieldTitle.indexOf('material') !== -1 || changedFieldTitle.indexOf('metal type') !== -1 || changedFieldTitle === 'metal') {
      if (candidates.indexOf('platinum') !== -1) {
        candidates.push('white');
      } else {
        // Fallback to the currently selected Color
        var currentColor = null;
        for (var si = 0; si < self.sections.length; si++) {
          var flds = self.sections[si].fields || [];
          for (var fi = 0; fi < flds.length; fi++) {
            var fTitle = (flds[fi].title || '').toLowerCase().trim();
            if (fTitle === 'color' || fTitle === 'metal color') {
               var sv = self.selections[flds[fi].id];
               if (sv) {
                  var fVals = flds[fi].values || [];
                  for (var fvi = 0; fvi < fVals.length; fvi++) {
                    if (fVals[fvi].value === sv) {
                      currentColor = (fVals[fvi].label || '').toLowerCase().trim();
                      break;
                    }
                  }
               }
            }
          }
        }
        if (currentColor) {
          candidates.push(currentColor);
        }
      }
    } else {
      if (candidates.indexOf('platinum') !== -1) {
        candidates.push('white');
      }
    }

    console.log('[DPO DEBUG] Search Candidates for Alt Text / Filename:', candidates);

    // ── Strategy 2: alt-text match via window.DPO_PRODUCT_MEDIA ──────────────
    var dpoMedia = w.DPO_PRODUCT_MEDIA || [];
    console.log('[DPO DEBUG] Available Product Images (DPO_PRODUCT_MEDIA):', dpoMedia);
    var found = null;

    for (var c = 0; c < candidates.length && !found; c++) {
      var term = candidates[c];
      for (var m = 0; m < dpoMedia.length; m++) {
        var alt = (dpoMedia[m].alt || '').toLowerCase().trim();
        if (alt && (alt === term || alt.indexOf(term) !== -1 || term.indexOf(alt) !== -1)) {
          found = dpoMedia[m];
          console.log('[DPO DEBUG] Strategy 2: Match found via Alt Text! Candidate:', term, 'matched Alt:', alt, 'Media:', found);
          break;
        }
      }
    }
    if (found) {
      this._setGalleryMedia(found.id);
      console.groupEnd();
      return;
    }

    // ── Strategy 3: filename match — swatch imageUrl vs gallery image src ─────
    if (selectedChoice && selectedChoice.imageUrl) {
      var swatchNorm = self._normUrl(selectedChoice.imageUrl);
      var swatchFile = swatchNorm.split('/').pop();
      console.log('[DPO DEBUG] Strategy 3: Attempting fallback filename matching. Swatch File:', swatchFile, 'Swatch URL:', swatchNorm);

      var galleryEl = d.querySelector('[id^="GalleryViewer-"]');
      if (galleryEl) {
        var mediaNodes = galleryEl.querySelectorAll('[data-media-id]');
        [].forEach.call(mediaNodes, function (node) {
          if (found) return;
          var imgEl = node.querySelector('img');
          if (!imgEl) return;
          var srcs = [
            imgEl.getAttribute('src'),
            imgEl.getAttribute('data-src')
          ];
          var srcset = imgEl.getAttribute('srcset') || imgEl.getAttribute('data-srcset') || '';
          srcset.split(',').forEach(function (entry) {
            srcs.push(entry.trim().split(' ')[0]);
          });
          srcs.forEach(function (rawSrc) {
            if (found || !rawSrc || rawSrc.indexOf('data:') === 0) return;
            var normSrc = self._normUrl(rawSrc);
            var normFile = normSrc.split('/').pop();
            if (normSrc === swatchNorm || (normFile && normFile === swatchFile)) {
              var dataId = node.getAttribute('data-media-id');
              var galleryViewerId = galleryEl.id.replace('GalleryViewer-', '');
              var numericId = galleryViewerId ? dataId.replace(galleryViewerId + '-', '') : dataId;
              found = { id: numericId };
              console.log('[DPO DEBUG] Strategy 3: Match found via Filename! Swatch matched Image Src:', normFile);
            }
          });
        });
      }
      if (found) {
        this._setGalleryMedia(found.id);
        console.groupEnd();
        return;
      } else {
        console.log('[DPO DEBUG] Strategy 3 Failed: Swatch URL didn\'t match any image on the page.');
      }
    }

    // ── Strategy 4: Smart Filename Text Match (Multi-Factor) ──────────────
    console.log('[DPO DEBUG] Strategy 4: Attempting smart multi-label match in image URLs');
    var galleryEl = d.querySelector('[id^="GalleryViewer-"]');
    if (galleryEl && !found) {
      // 1. Gather all currently selected labels across the entire configurator
      var activeLabels = [];
      for (var sec = 0; sec < (self.sections || []).length; sec++) {
        var fields = self.sections[sec].fields || [];
        for (var fld = 0; fld < fields.length; fld++) {
          var fldObj = fields[fld];
          var selVal = self.selections[fldObj.id];
          if (selVal && ["dropdown", "radio", "color_swatch", "image_swatch", "image_buttons"].indexOf(fldObj.type) !== -1) {
            var vals = fldObj.values || [];
            for (var vOpt = 0; vOpt < vals.length; vOpt++) {
              if (vals[vOpt].value === selVal) {
                var l = vals[vOpt].label.toLowerCase().replace(/[^a-z0-9]/g, '');
                if (l) activeLabels.push(l);
              }
            }
          }
        }
      }

      // Ensure the fresh candidate is heavily weighted
      var mainCandidate = candidates[0] ? candidates[0].toLowerCase().replace(/[^a-z0-9]/g, '') : '';
      console.log('[DPO DEBUG] Strategy 4: Active labels for scoring:', activeLabels);

      var bestMatch = null;
      var bestScore = -1;

      var mediaNodes = galleryEl.querySelectorAll('[data-media-id]');
      [].forEach.call(mediaNodes, function (node) {
        var imgEl = node.querySelector('img');
        if (!imgEl) return;
        var rawSrc = imgEl.getAttribute('data-src') || imgEl.getAttribute('src') || '';
        if (!rawSrc || rawSrc.indexOf('data:') === 0) return;

        // Normalize src string so we just evaluate the alphanumeric characters in the filename piece
        var urlObj = rawSrc.split('?')[0];
        var filename = urlObj.split('/').pop().toLowerCase();
        var normalizedFilename = filename.replace(/[^a-z0-9]/g, '');

        var score = 0;
        var matchedTerms = [];

        // Has to at least contain ONE of the candidates from the field that just changed
        var candidateMatched = false;
        for (var i = 0; i < candidates.length; i++) {
          var cNorm = candidates[i].toLowerCase().replace(/[^a-z0-9]/g, '');
          if (normalizedFilename.indexOf(cNorm) !== -1) {
            candidateMatched = true;
            score += 100; // heavy weight to the thing actually changing
            matchedTerms.push(candidates[i]);
            break;
          }
        }

        if (candidateMatched) {
          // Add score for other active contextual options (like color, material)
          activeLabels.forEach(function (lbl) {
            if (normalizedFilename.indexOf(lbl) !== -1) {
              score += 10;
              matchedTerms.push(lbl);
            }
          });

          console.log('[DPO DEBUG] Strategy 4 Evaluating:', filename, '| Score:', score, '| Matched:', matchedTerms.join(', '));
          if (score > bestScore) {
            bestScore = score;
            var dataId = node.getAttribute('data-media-id');
            var galleryViewerId = galleryEl.id.replace('GalleryViewer-', '');
            var numericId = galleryViewerId ? dataId.replace(galleryViewerId + '-', '') : dataId;
            bestMatch = { id: numericId, src: rawSrc, score: score, matched: matchedTerms };
          }
        }
      });

      if (bestMatch) {
        found = bestMatch;
        console.log('[DPO DEBUG] Strategy 4: BEST MATCH FOUND! Selected image matching:', bestMatch.matched.join(', '), '| src:', bestMatch.src);
      }
    }

    if (found) {
      this._setGalleryMedia(found.id);
      console.groupEnd();
      return;
    }

    console.warn('[DPO DEBUG] ERROR: No matching image could be found. Make sure Shopify product images have alt-text containing "' + candidates.join('" or "') + '"');
    console.groupEnd();
  };

  // Extract numeric ID from a Shopify GID or string
  Configurator.prototype._extractId = function (id) {
    if (!id) return null;
    var s = String(id);
    if (s.indexOf('/') === -1) return s;
    var parts = s.split('/');
    return parts[parts.length - 1];
  };

  // Apply show/hide based on current selections
  Configurator.prototype._applyVisibility = function () {
    var self = this;
    var visIds = this._visibleIds();
    
    var materialFieldId = null;
    var colorFieldId = null;
    var materialVal = null;

    (this.sections || []).forEach(function (sec) {
      (sec.fields || []).forEach(function (field) {
        var avTitle = (field.title || '').toLowerCase();
        if (avTitle.indexOf('material') !== -1 || avTitle.indexOf('metal') !== -1) {
          materialFieldId = field.id;
          materialVal = self.selections[field.id];
        }
        if (avTitle.indexOf('color') !== -1 && ["color_swatch", "image_swatch", "image_buttons"].indexOf(field.type) !== -1) {
          colorFieldId = field.id;
        }

        var fieldEl = self.container.querySelector('[data-field-id="' + field.id + '"]');
        if (!fieldEl) return;
        if (fieldEl.dataset.dpoLocked) return; // permanently hidden single-value field
        var shouldShow = visIds.indexOf(field.id) !== -1;
        // Keep visible if field already has a selection — mirrors the initial render logic.
        // This prevents size fields from disappearing after the user picks a different option.
        var hasSelection = self.selections[field.id] !== undefined &&
          self.selections[field.id] !== null &&
          self.selections[field.id] !== "";
        // Width fields with values are always shown when present in the DPO config
        // (their presence indicates this is a plain-ring product).
        var isWidthWithValues = avTitle.indexOf('width') !== -1 && (field.values || []).length > 0;
        var isPlainSwatchField = self._isPlainRing && (avTitle.indexOf('finishing') !== -1 || avTitle.indexOf('thickness') !== -1);
        var isThicknessField = avTitle.indexOf('thickness') !== -1;
        if (isThicknessField && !self._isPlainRing) {
          fieldEl.style.display = "none";
        } else {
          fieldEl.style.display = (shouldShow || hasSelection || isWidthWithValues || isPlainSwatchField) ? "" : "none";
        }
      });
    });

    // Custom rule: If Material == Platinum, disable all Color swatches except White
    if (materialFieldId && colorFieldId) {
      var isPlatinum = materialVal && materialVal.toLowerCase().indexOf('platinum') !== -1;
      var colorFieldEl = self.container.querySelector('[data-field-id="' + colorFieldId + '"]');
      if (colorFieldEl) {
        var swatches = colorFieldEl.querySelectorAll('button[data-value]');
        var whiteValFound = false;
        var firstWhiteValue = null;
        var whiteLabels = ["white gold", "white", "platinum", "silver"];
        
        swatches.forEach(function(sw) {
          var val = sw.dataset.value.toLowerCase();
          var label = sw.getAttribute('aria-label') ? sw.getAttribute('aria-label').toLowerCase().trim() : val;
          var isWhite = whiteLabels.indexOf(label) !== -1;
          
          // Also match if it only says 'white' and doesn't contain yellow/rose etc.
          if (!isWhite && label.indexOf('white') !== -1 && label.indexOf('yellow') === -1 && label.indexOf('rose') === -1 && label.indexOf('-') === -1 && label.indexOf('tone') === -1) {
            isWhite = true;
          }

          if (isPlatinum) {
            if (!isWhite) {
              sw.disabled = true;
              sw.style.opacity = '0.3';
              sw.style.cursor = 'not-allowed';
              sw.style.pointerEvents = 'none';
            } else {
              sw.disabled = false;
              sw.style.opacity = '';
              sw.style.cursor = '';
              sw.style.pointerEvents = '';
              whiteValFound = true;
              if (!firstWhiteValue) firstWhiteValue = sw.dataset.value;
            }
          } else {
            sw.disabled = false;
            sw.style.opacity = '';
            sw.style.cursor = '';
            sw.style.pointerEvents = '';
          }
        });

        // If platinum is selected and current color is not white, auto-select white
        if (isPlatinum && whiteValFound) {
          var currentColorVal = self.selections[colorFieldId];
          var currentColorBtn = currentColorVal ? colorFieldEl.querySelector('button[data-value="' + currentColorVal.replace(/"/g, '\\"') + '"]') : null;
          
          var currentColorIsWhite = false;
          if (currentColorBtn) {
            var cLabel = currentColorBtn.getAttribute('aria-label') ? currentColorBtn.getAttribute('aria-label').toLowerCase().trim() : currentColorVal.toLowerCase();
            currentColorIsWhite = whiteLabels.indexOf(cLabel) !== -1 || (cLabel.indexOf('white') !== -1 && cLabel.indexOf('yellow') === -1 && cLabel.indexOf('rose') === -1 && cLabel.indexOf('-') === -1 && cLabel.indexOf('tone') === -1);
          }

          if (!currentColorIsWhite && firstWhiteValue && !self._handlingPlatinumColorSync) {
            self._handlingPlatinumColorSync = true;
            setTimeout(function() {
              var whiteBtn = colorFieldEl.querySelector('button[data-value="' + firstWhiteValue.replace(/"/g, '\\"') + '"]');
              if (whiteBtn) whiteBtn.click();
              self._handlingPlatinumColorSync = false;
            }, 10);
          }
        }
      }
    }
  };

  // Update displayed price via backend calculate-price API.
  // Offer/discount math (compare-at + save badge) is computed on the frontend
  // from the API price — no separate backend call for that.
  Configurator.prototype._updatePrice = function () {
    var self = this;
    var variantSelections = this._getVariantSelections();
    var requestId = ++this._priceRequestId;
    this.priceReady = false;

    // Keep the last-known (or base) price visible, just dimmed, instead of
    // blanking it to "Loading..." — avoids the page looking broken/frozen
    // while a reprice is in flight, on both initial load and every option
    // change. The Add to Cart button is intentionally left enabled too:
    // _handleAddToCart() already guards on this.priceReady (set to false
    // above) and shows a friendly "please wait" message rather than
    // submitting a stale price if clicked mid-reprice, so nothing here
    // risks adding the wrong price/variant to the cart.
    var priceEl = d.getElementById("dpo-price-" + this.config.id);
    if (priceEl) {
      priceEl.style.opacity = "0.55";
    }
    setThemePriceLoading();

    fetch(host() + "/calculate-price", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        product_id: this.config.productId || this.config.id,
        selections: this.selections
      })
    })
      .then(function (res) { return res.json(); })
      .then(function (result) {
        if (requestId !== self._priceRequestId) return;
        if (result.error) { console.error("Pricing Error:", result.error); return; }

        self.price = result.price;
        self.priceReady = true;

        // Compute display (sale) price by applying frontend discount to the full API price
        var discountWrap = d.querySelector('.lwb-discount-price[data-discount-pct]');
        var displayPrice = parseFloat(result.price) || 0;
        self.compareAtPrice = null;
        if (discountWrap) {
          var pct = parseFloat(discountWrap.getAttribute('data-discount-pct')) || 0;
          if (pct > 0) {
            var fullPrice  = displayPrice;
            var salePrice  = fullPrice * ((100 - pct) / 100);
            var saveAmount = fullPrice - salePrice;
            displayPrice   = salePrice;
            self.compareAtPrice = fullPrice;
            var saleEl    = d.getElementById('lwb-sale-price');
            var compareEl = d.getElementById('lwb-compare-price');
            var badgeEl   = d.getElementById('lwb-save-badge');
            if (saleEl)    saleEl.textContent    = fmt(salePrice);
            if (compareEl) compareEl.textContent = fmt(fullPrice);
            if (badgeEl)   badgeEl.textContent   = 'SAVE ' + fmt(saveAmount);
          }
        }

        self.displayPrice = displayPrice;
        if (priceEl) { priceEl.textContent = fmt(displayPrice); priceEl.style.opacity = "1"; }

        // Sync to Shopify price elements
        d.querySelectorAll(
          '.product__info-container .price-item--regular:not(s), ' +
          '.product__info-container .price-item--sale'
        ).forEach(function (el) { el.textContent = fmt(displayPrice); });

        var breakdownEl = d.getElementById("dpo-breakdown-" + self.config.id);
        if (breakdownEl) {
          var rows = Object.keys(result.breakdown || {})
            .filter(function (k) { return result.breakdown[k] !== 0 || k === "Base Price"; })
            .map(function (k) {
              var v = result.breakdown[k];
              return '<div class="dpo-bd-row"><span>' + k + '</span><span>' + (v >= 0 ? "+" : "") + fmt(v) + '</span></div>';
            });
          breakdownEl.innerHTML = rows.join("");
        }

        var btn = d.getElementById("dpo-atc-btn-" + self.config.id);
        if (btn) {
          self._lastBtnPrice = displayPrice;
          // Enable as soon as the price/variant are known — no need to wait
          // for the browser's window.load (all images/resources finished
          // downloading) too. The button's own DOM and the gallery image
          // element used at add-to-cart time only need to be parsed, not
          // fully loaded, so this used to add an unnecessary extra wait on
          // image-heavy product pages.
          setAtcBtnState(btn, "Add to Cart - " + fmt(displayPrice), false);
        }

        // Precompute variant for instant Add to Cart
        var currentConfigStr = JSON.stringify(variantSelections);
        self.currentSelectionsConfig = currentConfigStr;

        self.currentVariantPromise = fetch(host() + "/checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            product_id: self.config.id || self.config.productId || self.container.dataset.productId,
            selections: self._getCheckoutSelections(),
            precomputedPrice: displayPrice,
            shop: self.container.dataset.shopDomain || (w.Shopify && w.Shopify.shop)
          })
        })
          .then(function (r) { if (!r.ok) throw new Error("Server error " + r.status); return r.json(); })
          .then(function (data) {
            if (!data.variantId) throw new Error(data.error || "No variant returned");
            self._updateGalleryForVariant(data.variantId);
            return data;
          })
          .catch(function (err) { console.error("Precompute error:", err); return null; });
      })
      .catch(function (err) {
        if (requestId !== self._priceRequestId) return;
        console.error("Network Error in Pricing:", err);
        if (priceEl) {
          priceEl.textContent = fmt(self.price || self.config.basePrice || 0);
          priceEl.style.opacity = "1";
        }
        var btn = d.getElementById("dpo-atc-btn-" + self.config.id);
        if (btn) {
          setAtcBtnState(btn, "Add to Cart - " + fmt(self.displayPrice || self.price || self.config.basePrice || 0), false);
        }
        self.priceReady = true;
      });
  };

  // ── Gallery Image Sync ────────────────────────────────────────────────────
  // When the configurator resolves a variant from the /checkout endpoint,
  // this method finds the variant's featured_media from the preloaded product
  // JSON and updates the theme's media gallery to show the matching image.

  Configurator.prototype._updateGalleryForVariant = function (variantId) {
    try {
      // ── Strategy 1: Use variant data already in the page ──────────────────
      // The theme embeds all variants as JSON inside variant-radios / variant-selects.
      // Even though we hide those elements, the script tag is still in the DOM.
      var variantDataEl = d.querySelector(
        'variant-radios [type="application/json"], ' +
        'variant-selects [type="application/json"], ' +
        '.product-form__noscript-wrapper [type="application/json"]'
      );

      var matched = null;
      if (variantDataEl) {
        try {
          var variants = JSON.parse(variantDataEl.textContent);
          for (var i = 0; i < variants.length; i++) {
            if (String(variants[i].id) === String(variantId)) {
              matched = variants[i];
              break;
            }
          }
        } catch (e) {
          // Ignore
        }
      }

      // ── Guard: don't overwrite a specific finishing/color image ───────────
      // If _syncGalleryFromSelections already set a non-default image (e.g. "Yellow
      // Matte", "Rose Gold"), skip this async update entirely. The async variant
      // data often points to a generic White Gold image, which would overwrite the
      // finishing image the user just selected.
      var activeSlide = d.querySelector('[id^="GalleryViewer-"] .is-active[data-media-id]');
      var skipOverride = false;
      if (activeSlide) {
        var activeKey = activeSlide.getAttribute('data-media-id');
        var activeNumId = activeKey.split('-').pop();
        var dpoArr = w.DPO_PRODUCT_MEDIA || [];
        var activeAlt = '';
        for (var gi = 0; gi < dpoArr.length; gi++) {
          if (String(dpoArr[gi].id) === activeNumId) { activeAlt = (dpoArr[gi].alt || '').toLowerCase().trim(); break; }
        }
        if (activeAlt && activeAlt !== 'default products image') {
          skipOverride = true;
        }
      }

      if (matched && matched.featured_media && matched.featured_media.id) {
        var fmId1 = String(matched.featured_media.id);
        var dpoMedia1 = w.DPO_PRODUCT_MEDIA || [];
        var fmAlt1 = '';
        for (var di = 0; di < dpoMedia1.length; di++) {
          if (String(dpoMedia1[di].id) === fmId1) { fmAlt1 = (dpoMedia1[di].alt || '').toLowerCase().trim(); break; }
        }
        
        // If the variant's featured media alt specifically contains "platinum", we should allow it to override!
        if (fmAlt1.indexOf('platinum') !== -1) {
            skipOverride = false;
        }

        if (skipOverride) {
          console.log('[DPO] _updateGalleryForVariant: gallery shows specific image ("' + activeAlt + '"), skipping async override.');
          return;
        }

        if (fmAlt1 !== 'default products image') {
          this._setGalleryMedia(matched.featured_media.id);
        }
        return;
      }
      
      if (skipOverride) {
        return;
      }

      // ── Strategy 2: Fetch the section HTML for the variant ────────────────
      // Determine the section ID from the MediaGallery element
      var galleryEl = d.querySelector('[id^="MediaGallery-"]');
      var sectionId = galleryEl
        ? galleryEl.id.replace('MediaGallery-', '')
        : null;

      if (!sectionId) {
        // Try grabbing section ID from the product-info element
        var productInfo = d.querySelector('product-info[data-section]');
        sectionId = productInfo ? productInfo.dataset.section : null;
      }

      if (!sectionId) {
        console.warn('[DPO] Could not determine section ID for gallery update');
        return;
      }

      var productUrl = (w.location.pathname.indexOf('/products/') !== -1)
        ? w.location.pathname
        : null;
      if (!productUrl) return;

      var self = this;
      fetch(productUrl + '?variant=' + variantId + '&section_id=' + sectionId)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          // The section re-render contains the updated variant JSON
          var updatedVariantEl = parsed.querySelector(
            'variant-radios [type="application/json"], ' +
            'variant-selects [type="application/json"]'
          );
          if (!updatedVariantEl) return;
          var updatedVariants = JSON.parse(updatedVariantEl.textContent);
          var matchedVariant = null;
          for (var j = 0; j < updatedVariants.length; j++) {
            if (String(updatedVariants[j].id) === String(variantId)) {
              matchedVariant = updatedVariants[j];
              break;
            }
          }
          if (matchedVariant && matchedVariant.featured_media && matchedVariant.featured_media.id) {
            var fmId2 = String(matchedVariant.featured_media.id);
            var dpoMedia2 = w.DPO_PRODUCT_MEDIA || [];
            var fmAlt2 = '';
            for (var dj = 0; dj < dpoMedia2.length; dj++) {
              if (String(dpoMedia2[dj].id) === fmId2) { fmAlt2 = (dpoMedia2[dj].alt || '').toLowerCase().trim(); break; }
            }
            if (fmAlt2 !== 'default products image') {
              self._setGalleryMedia(matchedVariant.featured_media.id);
            }
          }
        })
        .catch(function (err) {
          console.warn('[DPO] Gallery sync fetch failed:', err);
        });
    } catch (e) {
      console.warn('[DPO] Gallery sync error:', e);
    }
  };

  // Call setActiveMedia on every media gallery on the page
  Configurator.prototype._setGalleryMedia = function (mediaId) {
    if (!mediaId) return;
    var numericId = this._extractId(mediaId);
    console.log('[DPO] _setGalleryMedia numericId:', numericId);

    d.querySelectorAll('[id^="MediaGallery-"]').forEach(function (gallery) {
      var sectionId = gallery.id.replace('MediaGallery-', '');
      var mediaKey = sectionId + '-' + numericId;
      console.log('[DPO] Calling setActiveMedia with key:', mediaKey);

      // Try the theme's native setActiveMedia first
      if (typeof gallery.setActiveMedia === 'function') {
        try {
          gallery.setActiveMedia(mediaKey, true);
          return;
        } catch (e) {
          console.warn('[DPO] setActiveMedia threw:', e.message);
        }
      }

      // Fallback: directly toggle is-active and scroll to the target item
      var viewer = gallery.querySelector('[id^="GalleryViewer-"]');
      if (!viewer) return;
      var target = viewer.querySelector('[data-media-id="' + mediaKey + '"]');
      if (!target) {
        console.warn('[DPO] No element found for data-media-id="' + mediaKey + '"');
        return;
      }
      viewer.querySelectorAll('[data-media-id]').forEach(function (el) {
        el.classList.remove('is-active');
      });
      target.classList.add('is-active');
      var isVariant = target.classList.contains('dpo-variant-image') || target.classList.contains('product__media-item--variant');
      var isColumns = gallery.classList.contains('product--columns') || gallery.classList.contains('lwb-his-and-her-gallery-wrapper');
      if (!isVariant || isColumns) {
        target.parentElement.prepend(target);
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
    this._updateGridLayout();
  };

  Configurator.prototype._getSelectedColor = function () {
    var colorField = null;
    for (var s = 0; s < this.sections.length; s++) {
      var fields = this.sections[s].fields || [];
      for (var f = 0; f < fields.length; f++) {
        var title = (fields[f].title || '').toLowerCase();
        if (title.indexOf('color') !== -1 || title.indexOf('metal') !== -1) {
          colorField = fields[f];
          break;
        }
      }
      if (colorField) break;
    }
    if (!colorField) return '';
    var selectedValue = this.selections[colorField.id];
    if (!selectedValue) return '';
    var vals = colorField.values || [];
    for (var v = 0; v < vals.length; v++) {
      if (vals[v].value === selectedValue) {
        return vals[v].label.toLowerCase().trim();
      }
    }
    return String(selectedValue).toLowerCase().trim();
  };

  Configurator.prototype._getWhiteGoldMediaId = function () {
    var variantDataEl = d.querySelector(
      'variant-radios [type="application/json"], ' +
      'variant-selects [type="application/json"]'
    );
    if (!variantDataEl) return null;
    try {
      var variants = JSON.parse(variantDataEl.textContent);
      for (var i = 0; i < variants.length; i++) {
        var isWhite = false;
        if (variants[i].options) {
          for (var o = 0; o < variants[i].options.length; o++) {
            if (String(variants[i].options[o]).toLowerCase().indexOf('white') !== -1) {
              isWhite = true;
              break;
            }
          }
        }
        if (isWhite && variants[i].featured_media && variants[i].featured_media.id) {
          return String(variants[i].featured_media.id);
        }
      }
    } catch(e) {}
    return null;
  };

  Configurator.prototype._updateGridLayout = function () {
    var galleryEl = d.querySelector('[id^="MediaGallery-"]');
    if (!galleryEl) return;
    var isColumns = galleryEl.getAttribute('data-desktop-layout') === 'columns' || galleryEl.classList.contains('lwb-his-and-her-gallery-wrapper');
    if (!isColumns) return;

    var viewer = galleryEl.querySelector('[id^="Slider-Gallery-"]');
    if (!viewer) return;

    var isDesktop = window.matchMedia('(min-width: 750px)').matches;

    // Remove cloned items first
    viewer.querySelectorAll('.product__media-item.dpo-cloned-item').forEach(function (el) {
      el.parentNode && el.parentNode.removeChild(el);
    });

    var originalSlides = Array.prototype.slice.call(viewer.querySelectorAll('.product__media-item'));

    if (!isDesktop && !galleryEl.classList.contains('lwb-his-and-her-gallery-wrapper')) {
      // Restore all original slides for mobile slider
      originalSlides.forEach(function (slide) {
        slide.classList.remove('dpo-hide');
        slide.style.display = '';
      });
      return;
    }

    // Desktop Columns layout - 2x2 Grid
    var activeColor = this._getSelectedColor();
    var self = this;

    function getAltForSlide(slide) {
      var mediaIdAttr = slide.getAttribute('data-media-id') || '';
      var numId = mediaIdAttr.split('-').pop();
      var dpoMedia = window.DPO_PRODUCT_MEDIA || [];
      for (var i = 0; i < dpoMedia.length; i++) {
        if (String(dpoMedia[i].id) === String(numId)) {
          return (dpoMedia[i].alt || '').toLowerCase().trim();
        }
      }
      var img = slide.querySelector('img');
      return img ? (img.getAttribute('alt') || '').toLowerCase().trim() : '';
    }

    // Get all option color labels
    var allColors = [];
    for (var s = 0; s < self.sections.length; s++) {
      var fields = self.sections[s].fields || [];
      for (var f = 0; f < fields.length; f++) {
        var title = (fields[f].title || '').toLowerCase();
        if (title.indexOf('color') !== -1 || title.indexOf('metal') !== -1) {
          var vals = fields[f].values || [];
          for (var v = 0; v < vals.length; v++) {
            allColors.push(vals[v].label.toLowerCase().trim());
          }
        }
      }
    }
    if (!allColors.length) {
      allColors = ['yellow gold', 'white gold', 'rose gold'];
    }

    var activeVariantSlide = null;
    var whiteGoldSlide = null;
    var wornSlides = [];

    // 1. Find active variant slide (the one currently set to is-active by the configurator)
    activeVariantSlide = viewer.querySelector('.product__media-item.is-active:not(.dpo-cloned-item)');
    if (!activeVariantSlide) {
      for (var i = 0; i < originalSlides.length; i++) {
        var alt = getAltForSlide(originalSlides[i]);
        if (alt.indexOf(activeColor) !== -1) {
          activeVariantSlide = originalSlides[i];
          break;
        }
      }
    }
    if (!activeVariantSlide && originalSlides.length > 0) {
      activeVariantSlide = originalSlides[0];
    }

    // 2. Find white gold variant slide (empty ring)
    var whiteGoldMediaId = self._getWhiteGoldMediaId();
    if (whiteGoldMediaId) {
      for (var i = 0; i < originalSlides.length; i++) {
        var mId = originalSlides[i].getAttribute('data-media-id') || '';
        if (mId.split('-').pop() === whiteGoldMediaId) {
          whiteGoldSlide = originalSlides[i];
          break;
        }
      }
    }
    if (!whiteGoldSlide) {
      for (var i = 0; i < originalSlides.length; i++) {
        var alt = getAltForSlide(originalSlides[i]);
        var isColorVariant = false;
        for (var c = 0; c < allColors.length; c++) {
          if (alt.indexOf(allColors[c]) !== -1) {
            isColorVariant = true;
            break;
          }
        }
        if (isColorVariant && alt.indexOf('white') !== -1 && alt.indexOf('default products image') === -1) {
          whiteGoldSlide = originalSlides[i];
          break;
        }
      }
    }
    if (!whiteGoldSlide) {
      for (var i = 0; i < originalSlides.length; i++) {
        var alt = getAltForSlide(originalSlides[i]);
        if (alt.indexOf('white') !== -1) {
          whiteGoldSlide = originalSlides[i];
          break;
        }
      }
    }

    // Helper to identify worn/lifestyle slides by alt text keywords
    function isWornSlide(slide, alt) {
      var altLc = alt || getAltForSlide(slide);
      var keywords = ['worn', 'hand', 'couple', 'lifestyle', 'model', 'wear', 'show', 'on', 'people', 'man', 'woman', 'finger'];
      for (var k = 0; k < keywords.length; k++) {
        if (altLc.indexOf(keywords[k]) !== -1) return true;
      }
      return false;
    }

    // 3. Find worn/neutral slides (prioritize matching activeColor, fallback to neutral, fallback to other colors)
    var activeWornSlides = [];
    var neutralWornSlides = [];
    var otherWornSlides = [];

    for (var i = 0; i < originalSlides.length; i++) {
      var alt = getAltForSlide(originalSlides[i]);
      var isWorn = isWornSlide(originalSlides[i], alt);
      var isColorVariant = false;
      for (var c = 0; c < allColors.length; c++) {
        if (alt.indexOf(allColors[c]) !== -1) {
          isColorVariant = true;
          break;
        }
      }

      if (isWorn) {
        if (alt.indexOf(activeColor) !== -1) {
          activeWornSlides.push(originalSlides[i]);
        } else if (!isColorVariant) {
          neutralWornSlides.push(originalSlides[i]);
        } else {
          otherWornSlides.push(originalSlides[i]);
        }
      } else {
        if (!isColorVariant && alt.indexOf('default products image') === -1) {
          neutralWornSlides.push(originalSlides[i]);
        }
      }
    }

    var finalWorn = [].concat(activeWornSlides, neutralWornSlides, otherWornSlides);
    var slot1 = activeVariantSlide;
    var slot2 = finalWorn[0] || null;
    var slot3 = finalWorn[1] || null;
    var slot4 = null;

    // We clone slot1 (active variant empty ring) to Slot 4 so that BOTH Slot 1 and Slot 4
    // show the active variant color and finishing ring, matching the selected configuration dynamically.
    if (slot1) {
      slot4 = slot1.cloneNode(true);
      slot4.classList.add('dpo-cloned-item');
      slot4.classList.remove('dpo-hide');
      slot4.style.display = '';
      slot4.removeAttribute('id');
      slot4.querySelectorAll('[id]').forEach(function (el) {
        el.removeAttribute('id');
      });
    }

    var toShow = [];
    if (slot1) toShow.push(slot1);
    if (slot2) toShow.push(slot2);
    if (slot3) toShow.push(slot3);
    if (slot4) toShow.push(slot4);

    originalSlides.forEach(function (slide) {
      if (toShow.indexOf(slide) !== -1) {
        slide.classList.remove('dpo-hide');
        slide.style.display = '';
      } else {
        slide.classList.add('dpo-hide');
      }
    });

    toShow.forEach(function (slide) {
      viewer.appendChild(slide);
    });
  };

  // ── Individual field builders ──────────────────────────────────────────────

  Configurator.prototype._mkTextInput = function (field) {
    var self = this;
    var inp = el("input", "dpo-input dpo-text-input", { type: "text", placeholder: "Enter " + field.title + "…", autocomplete: "off" });
    if (field.maxLength) inp.maxLength = field.maxLength;
    if (field.required) inp.required = true;
    if (this.selections[field.id]) inp.value = this.selections[field.id];
    inp.addEventListener("input", function () { self._onChange(field.id, inp.value); });
    if (field.hideOnFocus) {
      inp.addEventListener("focus", function () { inp.previousElementSibling && (inp.previousElementSibling.style.opacity = "0"); });
      inp.addEventListener("blur", function () { inp.previousElementSibling && (inp.previousElementSibling.style.opacity = "1"); });
    }
    return inp;
  };

  Configurator.prototype._mkTextarea = function (field) {
    var self = this;
    var ta = el("textarea", "dpo-textarea");
    ta.placeholder = "Enter " + field.title + "…";
    ta.rows = 3;
    if (field.maxLength) ta.maxLength = field.maxLength;
    if (field.required) ta.required = true;
    if (this.selections[field.id]) ta.value = this.selections[field.id];
    ta.addEventListener("input", function () { self._onChange(field.id, ta.value); });
    return ta;
  };

  Configurator.prototype._mkNumber = function (field) {
    var self = this;
    var inp = el("input", "dpo-input dpo-number dpo-number-input", { type: "number", autocomplete: "off" });
    if (field.required) inp.required = true;
    if (this.selections[field.id] !== undefined) inp.value = this.selections[field.id];
    inp.addEventListener("change", function () { self._onChange(field.id, parseFloat(inp.value) || 0); });
    return inp;
  };

  Configurator.prototype._mkDate = function (field) {
    var self = this;
    var inp = el("input", "dpo-input dpo-date dpo-date-input", { type: "date" });
    if (field.required) inp.required = true;
    if (this.selections[field.id]) inp.value = this.selections[field.id];
    inp.addEventListener("change", function () { self._onChange(field.id, inp.value); });
    return inp;
  };

  Configurator.prototype._mkDropdown = function (field) {
    var self = this;
    var sel = el("select", "dpo-select");
    if (field.required) sel.required = true;
    var ph = el("option"); ph.value = ""; ph.textContent = "Select " + field.title + "…"; sel.appendChild(ph);
    (field.values || []).forEach(function (v) {
      var opt = el("option"); opt.value = v.value;
      opt.textContent = v.label;
      if (self.selections[field.id] === v.value) opt.selected = true;
      sel.appendChild(opt);
    });
    sel.addEventListener("change", function () { self._onChange(field.id, sel.value); });
    return sel;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // _mkSliderControl — pure manual JS slider using native scrollLeft.
  // No CSS transforms, no flex fighting, no overflow battles.
  // The viewport (vp) is a hidden-scrollbar scroll container.
  // Arrows change currentIdx and set vp.scrollLeft — that's it.
  // ─────────────────────────────────────────────────────────────────────────
  Configurator.prototype._mkSliderControl = function (buttonSelector, buildTrack, valueCount, maxBtnWidth) {
    var MAX_VIS_DESKTOP = 5;
    var MAX_VIS_MOBILE  = 3;
    var GAP = 8; // must match CSS gap on .dpo-swiper-track

    var count = valueCount || 0;

    // ── Fast path: ≤3 values → plain flex row, no slider needed ──────────────
    if (count > 0 && count <= MAX_VIS_MOBILE) {
      var row = el('div', 'dpo-radio-grp');
      buildTrack(row);
      return row;
    }

    // ── Build DOM ─────────────────────────────────────────────────────────────
    var wrap  = el('div',    'dpo-swiper-wrap');
    var prev  = el('button', 'dpo-swiper-nav dpo-swiper-prev', { type: 'button', 'aria-label': 'Previous' });
    var vp    = el('div',    'dpo-swiper-vp');
    var track = el('div',    'dpo-swiper-track');
    var next  = el('button', 'dpo-swiper-nav dpo-swiper-next', { type: 'button', 'aria-label': 'Next' });

    prev.innerHTML = '&#8249;';
    next.innerHTML = '&#8250;';
    prev.style.display = 'none';
    next.style.display = 'none';

    buildTrack(track);
    vp.appendChild(track);
    wrap.appendChild(prev);
    wrap.appendChild(vp);
    wrap.appendChild(next);

    // ── State ─────────────────────────────────────────────────────────────────
    var currentIdx = 0; // index of the first visible button

    function getMaxVisible() {
      return window.innerWidth <= 767 ? MAX_VIS_MOBILE : MAX_VIS_DESKTOP;
    }

    function getBtns() {
      return Array.prototype.slice.call(track.querySelectorAll(buttonSelector));
    }

    // ── layout: measure → size buttons → scroll to currentIdx ────────────────
    function layout() {
      var btns   = getBtns();
      var maxVis = getMaxVisible();
      var vpW    = vp.offsetWidth;

      if (!vpW || !btns.length) return;

      var needsSlider = btns.length > maxVis;

      // Per-button pixel width
      var btnW = 0;
      if (needsSlider) {
        btnW = Math.floor((vpW - GAP * (maxVis - 1)) / maxVis);
        if (maxBtnWidth) btnW = Math.min(btnW, maxBtnWidth);
      } else if (maxBtnWidth) {
        btnW = maxBtnWidth;
      }

      // Apply uniform width to every button
      btns.forEach(function (btn) {
        if (btnW) {
          btn.style.width    = btnW + 'px';
          btn.style.minWidth = btnW + 'px';
          btn.style.maxWidth = btnW + 'px';
        } else {
          btn.style.width = btn.style.minWidth = btn.style.maxWidth = '';
        }
      });

      // Show / hide navigation arrows
      prev.style.display = needsSlider ? '' : 'none';
      next.style.display = needsSlider ? '' : 'none';

      // Clamp index to valid range
      var maxIdx = Math.max(0, btns.length - maxVis);
      if (currentIdx > maxIdx) currentIdx = maxIdx;

      // Set scroll position — native scrollLeft, no transforms
      vp.scrollLeft = currentIdx * (btnW + GAP);

      // Disable arrows at the ends
      prev.disabled = currentIdx <= 0;
      next.disabled = currentIdx >= maxIdx;
    }

    // ── Arrow click handlers ──────────────────────────────────────────────────
    prev.addEventListener('click', function () {
      if (currentIdx > 0) { currentIdx--; layout(); }
    });

    next.addEventListener('click', function () {
      var maxIdx = Math.max(0, getBtns().length - getMaxVisible());
      if (currentIdx < maxIdx) { currentIdx++; layout(); }
    });

    // ── Run after first paint, on page load, and on every resize ─────────────
    setTimeout(layout, 0);
    window.addEventListener('load',   layout);
    window.addEventListener('resize', layout);

    return wrap;
  };

  Configurator.prototype._mkRadio = function (field) {
    var self    = this;
    var values  = field.values || [];
    var titleLc = (field.title || '').toLowerCase();

    // WIDTH field: plain wrapping flex row — no slider, no carousel, no overflow
    if (titleLc.indexOf('width') !== -1) {
      var row = el('div', 'dpo-radio-grp dpo-radio-grp--width');
      values.forEach(function (v) {
        var btn = el('button', 'dpo-radio-btn dpo-width-btn', { type: 'button' });
        btn.dataset.value = v.value;
        btn.textContent   = v.label;
        if (self.selections[field.id] === v.value) btn.classList.add('active');
        btn.addEventListener('click', function () {
          row.querySelectorAll('.dpo-width-btn').forEach(function (b) { b.classList.remove('active'); });
          btn.classList.add('active');
          self._onChange(field.id, v.value);
        });
        row.appendChild(btn);
      });
      return row;
    }

    // All other radio fields: use the scrollLeft slider
    return this._mkSliderControl('.dpo-radio-btn', function (track) {
      values.forEach(function (v) {
        var btn = el('button', 'dpo-radio-btn', { type: 'button' });
        btn.dataset.value = v.value;
        btn.innerHTML = v.label;
        if (self.selections[field.id] === v.value) btn.classList.add('active');
        btn.addEventListener('click', function () {
          track.querySelectorAll('.dpo-radio-btn').forEach(function (b) { b.classList.remove('active'); });
          btn.classList.add('active');
          self._onChange(field.id, v.value);
        });
        track.appendChild(btn);
      });
    }, values.length, null);
  };

  Configurator.prototype._mkCheckbox = function (field) {
    var self = this;
    var grp = el("div", "dpo-check-grp");
    var currentVals = Array.isArray(self.selections[field.id]) ? self.selections[field.id] : [];
    (field.values || []).forEach(function (v) {
      var row = el("div", "dpo-check-row");
      var cb = el("input", "dpo-checkbox", { type: "checkbox", name: field.id, value: v.value });
      if (currentVals.indexOf(v.value) !== -1) cb.checked = true;
      var lbl = el("label", "dpo-check-label");
      lbl.textContent = v.label;
      cb.addEventListener("change", function () {
        var checked = [];
        var inputs = grp.querySelectorAll("input:checked");
        for (var i = 0; i < inputs.length; i++) {
          checked.push(inputs[i].value);
        }
        self._onChange(field.id, checked);
      });
      row.appendChild(cb);
      row.appendChild(lbl);
      grp.appendChild(row);
    });
    return grp;
  };

  Configurator.prototype._findSwatchMedia = function (candidates) {
    var dpoMedia = window.DPO_PRODUCT_MEDIA || [];
    var foundMedia = null;

    // PASS 1: Exact matches
    for (var c = 0; c < candidates.length && !foundMedia; c++) {
      var term = candidates[c];
      var cNorm = term.replace(/[^a-z0-9]/g, '');
      if (!cNorm) continue;
      
      for (var m = 0; m < dpoMedia.length; m++) {
        var srcLc = (dpoMedia[m].src || '').toLowerCase();
        if (srcLc.indexOf('human_image') !== -1) continue;
        var alt = (dpoMedia[m].alt || '').toLowerCase().trim();
        if (alt.indexOf('lifestyle') !== -1 || alt.indexOf('human') !== -1) continue;

        var matched = false;
        if (alt && alt === term) {
          matched = true;
        }
        if (!matched && srcLc) {
          var urlObj = srcLc.split('?')[0];
          var filename = urlObj.split('/').pop();
          var nameNoExt = filename.split('.')[0].replace(/_[0-9]+x[0-9]*(?!.*_[0-9]+x[0-9]*)/, '');
          var fNormExact = nameNoExt.replace(/[^a-z0-9]/g, '');
          if (fNormExact === cNorm) matched = true;
        }
        if (matched && dpoMedia[m].mediaType && dpoMedia[m].mediaType !== 'image') matched = false;
        if (matched) { foundMedia = dpoMedia[m]; break; }
      }
    }
    if (foundMedia) return foundMedia;

    // PASS 2: Word boundary matches
    for (var c = 0; c < candidates.length && !foundMedia; c++) {
      var term = candidates[c];
      var cNorm = term.replace(/[^a-z0-9]/g, '');
      if (!cNorm) continue;

      for (var m = 0; m < dpoMedia.length; m++) {
        var srcLc = (dpoMedia[m].src || '').toLowerCase();
        if (srcLc.indexOf('human_image') !== -1) continue;
        var alt = (dpoMedia[m].alt || '').toLowerCase().trim();
        if (alt.indexOf('lifestyle') !== -1 || alt.indexOf('human') !== -1) continue;

        var matched = false;
        if (srcLc) {
          var urlObj = srcLc.split('?')[0];
          var filename = urlObj.split('/').pop();
          var nameNoExt = filename.split('.')[0].replace(/_[0-9]+x[0-9]*(?!.*_[0-9]+x[0-9]*)/, '');
          var fParts = nameNoExt.split(/[^a-z0-9]+/);
          if (fParts.indexOf(cNorm) !== -1) matched = true;
        }
        if (matched && dpoMedia[m].mediaType && dpoMedia[m].mediaType !== 'image') matched = false;
        if (matched) { foundMedia = dpoMedia[m]; break; }
      }
    }
    if (foundMedia) return foundMedia;

    // PASS 3: Original loose fallback
    for (var c = 0; c < candidates.length && !foundMedia; c++) {
      var term = candidates[c];
      var cNorm = term.replace(/[^a-z0-9]/g, '');
      if (!cNorm) continue;

      for (var m = 0; m < dpoMedia.length; m++) {
        var srcLc = (dpoMedia[m].src || '').toLowerCase();
        if (srcLc.indexOf('human_image') !== -1) continue;
        var alt = (dpoMedia[m].alt || '').toLowerCase().trim();
        if (alt.indexOf('lifestyle') !== -1 || alt.indexOf('human') !== -1) continue;

        var matched = false;
        if (alt && (alt.indexOf(term) !== -1 || term.indexOf(alt) !== -1)) matched = true;
        if (!matched && srcLc) {
          var urlObj = srcLc.split('?')[0];
          var filename = urlObj.split('/').pop();
          var fNorm = filename.replace(/[^a-z0-9]/g, '');
          if (fNorm.indexOf(cNorm) !== -1) matched = true;
        }
        if (matched && dpoMedia[m].mediaType && dpoMedia[m].mediaType !== 'image') matched = false;
        if (matched) { foundMedia = dpoMedia[m]; break; }
      }
    }
    return foundMedia;
  };

  // Helper to apply swatch styles with background color/gradient fallbacks natively in CSS
  Configurator.prototype._applySwatchStyle = function (thumb, imgUrl, mappedColor) {
    if (!imgUrl || imgUrl === "null" || imgUrl === "undefined") {
      thumb.style.background = mappedColor;
      return;
    }

    if (mappedColor.indexOf("gradient") !== -1) {
      thumb.style.backgroundImage = "url(" + imgUrl + "), " + mappedColor;
    } else {
      thumb.style.backgroundColor = mappedColor;
      thumb.style.backgroundImage = "url(" + imgUrl + ")";
    }
  };

  // ── Shared helper: build a dpo-finishing-strip of image/color swatch cards ──
  // Works for finishing, stone, gem, diamond, and any generic image_swatch field.
  // Shows text label inside the card when no imageUrl / colorHex is available.
  Configurator.prototype._mkSwatchStrip = function (field, colorHexFallback) {
    var self = this;
    var strip = el("div", "dpo-finishing-strip");
    var fTitle = (field.title || "").toLowerCase();
    var isStone = fTitle.indexOf("stone") !== -1 || fTitle.indexOf("gem") !== -1 || fTitle.indexOf("diamond") !== -1;
    var isPendant = false;
    if (w.DPO_PRODUCT_COLLECTIONS && w.DPO_PRODUCT_COLLECTIONS.some) {
      isPendant = w.DPO_PRODUCT_COLLECTIONS.some(function (c) {
        return c.indexOf("pendant") !== -1;
      });
    }

    (field.values || []).forEach(function (v) {
      var cardClasses = ["dpo-finishing-card"];
      if (isStone) cardClasses.push("dpo-stone-card");
      if (isPendant) cardClasses.push("dpo-pendant-card");

      var sw = el("button", cardClasses.join(" "), { type: "button", "aria-label": v.label });
      sw.dataset.value = v.value;
      sw.dataset.tooltip = v.label;

      var thumb = el("span", "dpo-finishing-card-thumb");
      var customImages = w.DPO_CUSTOM_SWATCH_IMAGES || {};
      var customUrl = customImages[v.label] || customImages[v.value] || null;
      var imgUrl = customUrl;

      var isColorField = colorHexFallback || (field.title || "").toLowerCase().indexOf("color") !== -1;
      if (!imgUrl && isColorField) {
        var candidates = [];
        if (v.label) candidates.push(v.label.toLowerCase().trim());
        if (v.value && typeof v.value === 'string' && v.value !== v.label) {
          candidates.push(v.value.toLowerCase().trim());
        }
        var foundMedia = self._findSwatchMedia(candidates);
        if (foundMedia && foundMedia.src) {
          imgUrl = foundMedia.src;
        }
      }
      if (!imgUrl) {
        imgUrl = v.imageUrl;
      }
      var fallbackColor = (colorHexFallback && v.colorHex) ? v.colorHex : null;

      if (imgUrl) {
        if (fallbackColor) {
          self._applySwatchStyle(thumb, imgUrl, fallbackColor);
        } else {
          thumb.style.backgroundImage = "url(" + imgUrl + ")";
        }
      } else {
        if (fallbackColor) {
          thumb.style.background = fallbackColor;
        } else {
          thumb.textContent = v.label.length > 8 ? v.label.slice(0, 7) + "…" : v.label;
        }
      }
      sw.appendChild(thumb);

      if (v.priceModifier) {
        var chip = el("span", "dpo-price-chip dpo-finishing-price");
        chip.textContent = (v.priceModifier > 0 ? "+" : "") + (v.priceType === "percentage" ? v.priceModifier + "%" : fmt(v.priceModifier));
        sw.appendChild(chip);
      }

      if (self.selections[field.id] === v.value) sw.classList.add("active");
      sw.addEventListener("click", function () {
        strip.querySelectorAll(".dpo-finishing-card").forEach(function (b) { b.classList.remove("active"); });
        sw.classList.add("active");
        self._onChange(field.id, v.value);
      });
      strip.appendChild(sw);
    });
    return strip;
  };

  Configurator.prototype._mkColorSwatch = function (field) {
    var self = this;
    var titleLcCol = (field.title || "").toLowerCase();

    // Stone / gem / diamond color swatches → image card strip
    if (titleLcCol.indexOf("stone") !== -1 || titleLcCol.indexOf("gem") !== -1 || titleLcCol.indexOf("diamond") !== -1) {
      return this._mkSwatchStrip(field, true);
    }

    // Color name → swatch background lookup (matches collection page CSS)
    var COLOR_MAP = {
      "white gold": "#e6e8e7", "white": "#e6e8e7",
      "yellow gold": "#e5cd96", "yellow": "#e5cd96",
      "rose gold": "#e0ac9a", "rose": "#e0ac9a",
      "platinum": "#b8b8cc", "silver": "#b8b8cc",
      "pink": "#f0b0a0",
      "two tone": "linear-gradient(135deg,#e5cd96 50%,#e6e8e7 50%)",
      "two-tone": "linear-gradient(135deg,#e5cd96 50%,#e6e8e7 50%)",
      "yellow-white": "linear-gradient(135deg,#e5cd96 50%,#e6e8e7 50%)",
      "rose-white": "linear-gradient(135deg,#e0ac9a 50%,#e6e8e7 50%)",
      "yellow white": "linear-gradient(135deg,#e5cd96 50%,#e6e8e7 50%)"
    };

    // Main color field → large ring-image cards
    if (titleLcCol.indexOf("color") !== -1) {
      var colorStrip = el("div", "dpo-color-strip");
      (field.values || []).forEach(function (v) {
        var sw = el("button", "dpo-color-card", { type: "button", "aria-label": v.label });
        sw.dataset.value = v.value;

        var thumb = el("span", "dpo-color-card-thumb");
        var customImages = w.DPO_CUSTOM_SWATCH_IMAGES || {};
        var customUrl = customImages[v.label] || customImages[v.value] || null;
        var imgUrl = customUrl;

        if (!imgUrl) {
          var candidates = [];
          if (v.label) candidates.push(v.label.toLowerCase().trim());
          if (v.value && typeof v.value === 'string' && v.value !== v.label) {
            candidates.push(v.value.toLowerCase().trim());
          }
          var foundMedia = self._findSwatchMedia(candidates);
          if (foundMedia && foundMedia.src) {
            imgUrl = foundMedia.src;
          }
        }
        
        if (!imgUrl) return;

        var labelKey = (v.label || v.value || "").toLowerCase().trim();
        var mappedColor = COLOR_MAP[labelKey] || v.colorHex || "#cccccc";

        self._applySwatchStyle(thumb, imgUrl, mappedColor);
        sw.appendChild(thumb);

        if (self.selections[field.id] === v.value) sw.classList.add("active");
        sw.addEventListener("click", function () {
          colorStrip.querySelectorAll(".dpo-color-card").forEach(function (b) { b.classList.remove("active"); });
          sw.classList.add("active");
          self._onChange(field.id, v.value);
        });
        colorStrip.appendChild(sw);
      });
      return colorStrip;
    }

    // Any other color_swatch → image card strip with colorHex fallback
    return this._mkSwatchStrip(field, true);
  };

  Configurator.prototype._mkImageSwatch = function (field) {
    var self = this;
    var titleLcImg = (field.title || "").toLowerCase();

    // Main color field with images → large ring-image cards
    if (titleLcImg.indexOf("color") !== -1) {
      var colorStrip = el("div", "dpo-color-strip");
      (field.values || []).forEach(function (v) {
        var sw = el("button", "dpo-color-card", { type: "button", "aria-label": v.label });
        sw.dataset.value = v.value;

        var thumb = el("span", "dpo-color-card-thumb");
        var customImages = w.DPO_CUSTOM_SWATCH_IMAGES || {};
        var customUrl = customImages[v.label] || customImages[v.value] || null;
        var imgUrl = customUrl;

        if (!imgUrl) {
          var candidates = [];
          if (v.label) candidates.push(v.label.toLowerCase().trim());
          if (v.value && typeof v.value === 'string' && v.value !== v.label) {
            candidates.push(v.value.toLowerCase().trim());
          }
          var foundMedia = self._findSwatchMedia(candidates);
          if (foundMedia && foundMedia.src) {
            imgUrl = foundMedia.src;
          }
        }
        
        if (!imgUrl) return;

        var imgLabelKey = (v.label || v.value || "").toLowerCase().trim();

        var IMG_COLOR_MAP = {
          "white gold": "#e6e8e7", "white": "#e6e8e7",
          "yellow gold": "#e5cd96", "yellow": "#e5cd96",
          "rose gold": "#e0ac9a", "rose": "#e0ac9a",
          "platinum": "#b8b8cc", "silver": "#b8b8cc",
          "pink": "#f0b0a0",
          "two tone": "linear-gradient(135deg,#e5cd96 50%,#e6e8e7 50%)",
          "two-tone": "linear-gradient(135deg,#e5cd96 50%,#e6e8e7 50%)",
          "yellow-white": "linear-gradient(135deg,#e5cd96 50%,#e6e8e7 50%)",
          "rose-white": "linear-gradient(135deg,#e0ac9a 50%,#e6e8e7 50%)",
          "yellow white": "linear-gradient(135deg,#e5cd96 50%,#e6e8e7 50%)"
        };
        var mappedColor = IMG_COLOR_MAP[imgLabelKey] || v.colorHex || "#cccccc";

        self._applySwatchStyle(thumb, imgUrl, mappedColor);
        sw.appendChild(thumb);

        if (self.selections[field.id] === v.value) sw.classList.add("active");
        sw.addEventListener("click", function () {
          colorStrip.querySelectorAll(".dpo-color-card").forEach(function (b) { b.classList.remove("active"); });
          sw.classList.add("active");
          self._onChange(field.id, v.value);
        });
        colorStrip.appendChild(sw);
      });
      return colorStrip;
    }

    // Material field → text pill buttons
    if (titleLcImg === "material") {
      var materialStrip = el("div", "dpo-material-strip");
      (field.values || []).forEach(function (v) {
        var sw = el("button", "dpo-material-card", { type: "button", "aria-label": v.label });
        sw.dataset.value = v.value;

        var labelEl = el("span", "dpo-material-card-label");
        labelEl.textContent = v.label;
        sw.appendChild(labelEl);


        if (self.selections[field.id] === v.value) sw.classList.add("active");
        sw.addEventListener("click", function () {
          materialStrip.querySelectorAll(".dpo-material-card").forEach(function (b) { b.classList.remove("active"); });
          sw.classList.add("active");
          self._onChange(field.id, v.value);
        });
        materialStrip.appendChild(sw);
      });
      return materialStrip;
    }

    // Everything else (finishing, stone, gem, diamond, any other image swatch) → card strip
    return this._mkSwatchStrip(field, false);
  };

  Configurator.prototype._mkFileUpload = function (field) {
    var self = this;
    var inp = el("input", "dpo-file", { type: "file" });
    inp.addEventListener("change", function () {
      self._onChange(field.id, inp.files[0] ? inp.files[0].name : "");
    });
    return inp;
  };

  Configurator.prototype._mkRangeSlider = function (field) {
    var self = this;
    var wrap = el("div", "dpo-range-wrap");
    var inp = el("input", "dpo-range", { type: "range", min: "0", max: "100", step: "1" });
    var display = el("span", "dpo-range-val");
    var initVal = self.selections[field.id] || 0;
    inp.value = initVal; display.textContent = initVal;
    inp.addEventListener("input", function () {
      display.textContent = inp.value;
      self._onChange(field.id, parseFloat(inp.value));
    });
    wrap.appendChild(inp);
    wrap.appendChild(display);
    return wrap;
  };

  Configurator.prototype._mkStepCounter = function (field) {
    var self = this;
    var wrap = el("div", "dpo-step-wrap");
    var val = parseInt(self.selections[field.id]) || 1;
    var minusBtn = el("button", "dpo-step-btn", { type: "button" });
    minusBtn.textContent = "−";
    var display = el("span", "dpo-step-val");
    display.textContent = val;
    var plusBtn = el("button", "dpo-step-btn", { type: "button" });
    plusBtn.textContent = "+";

    function update(n) {
      val = Math.max(1, n);
      display.textContent = val;
      self._onChange(field.id, val);
    }

    minusBtn.addEventListener("click", function () { update(val - 1); });
    plusBtn.addEventListener("click", function () { update(val + 1); });
    wrap.appendChild(minusBtn);
    wrap.appendChild(display);
    wrap.appendChild(plusBtn);
    return wrap;
  };

  // ── Add to Cart ────────────────────────────────────────────────────────────

  Configurator.prototype._handleAddToCart = function () {
    var self = this;
    var btn = d.getElementById("dpo-atc-btn-" + this.config.id);
    var errEl = d.getElementById("dpo-err-" + this.config.id);

    if (!this.priceReady) {
      if (errEl) { errEl.textContent = "Please wait for the price to finish loading."; errEl.style.display = "block"; }
      return;
    }

    // Validate required fields
    var allFields = [];
    (this.sections || []).forEach(function (s) {
      if (s.fields) {
        s.fields.forEach(function (f) { allFields.push(f); });
      }
    });
    var visIds = this._visibleIds();
    var missing = [];
    allFields.forEach(function (f) {
      if (f.required && visIds.indexOf(f.id) !== -1) {
        var sel = self.selections[f.id];
        if (!sel || (Array.isArray(sel) && !sel.length) || sel === "") {
          missing.push(f.title);
        }
      }
    });
    if (missing.length) {
      if (errEl) { errEl.textContent = "Please fill in: " + missing.join(", "); errEl.style.display = "block"; }
      return;
    }

    if (errEl) { errEl.style.display = "none"; }

    // Helper function to build line item properties
    function getProps() {
      var props = {};
      allFields.forEach(function (f) {
        if (visIds.indexOf(f.id) === -1) return;
        if (f.hideSkuInCart) return;
        var sel = self.selections[f.id];
        if (sel !== undefined && sel !== "") {
          props[f.title] = Array.isArray(sel) ? sel.join(", ") : String(sel);
        }
      });
      props["_config_price"] = "$" + roundUpMoney(self.price).toFixed(2);

      // Extract the currently active gallery image src to use in cart
      try {
        var activeSlide = d.querySelector('[id^="GalleryViewer-"] .is-active img, [id^="GalleryViewer-"] [data-media-id].is-active img, .product__media-list .is-active img, .product__media-item.is-active img');
        if (activeSlide) {
          var imgSrc = activeSlide.getAttribute('src') || activeSlide.getAttribute('data-src');
          if (imgSrc) {
            if (imgSrc.indexOf('//') === 0) {
              imgSrc = 'https:' + imgSrc;
            }
            props["_dpo_image"] = imgSrc;
            console.log('[DPO] Successfully captured active gallery image for cart:', imgSrc);
          }
        } else {
          console.warn('[DPO] No active gallery image slide found.');
        }
      } catch (err) {
        console.error('[DPO] Error capturing active gallery image:', err);
      }

      return props;
    }

    // Helper function to add to cart natively
    function addToCartWithRetry(variantId, props, retries) {
      var cd = d.querySelector('cart-drawer');
      var cn = d.querySelector('cart-notification');
      var cartDrawer = (cd && typeof cd.getSectionsToRender === 'function') ? cd : (cn && typeof cn.getSectionsToRender === 'function') ? cn : null;
      var formData = new FormData();
      formData.append('id', Number(variantId));
      formData.append('quantity', 1);
      
      for (var key in props) {
        if (props.hasOwnProperty(key)) {
          formData.append('properties[' + key + ']', props[key]);
        }
      }

      // Append sections so that /cart/add response includes section HTML needed by renderContents
      if (cartDrawer) {
        formData.append('sections', cartDrawer.getSectionsToRender().map(function (s) { return s.id; }).join(','));
        formData.append('sections_url', w.location.pathname);
        cartDrawer.setActiveElement(document.activeElement);
      }

      var cartAddUrl = (w.routes && w.routes.cart_add_url) || (typeof routes !== 'undefined' && routes.cart_add_url) || "/cart/add";

      return fetch(cartAddUrl, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: formData
      }).then(function (res) {
        if (res.ok) return res;
        if (retries > 0 && (res.status === 404 || res.status === 422)) {
          console.log("[DPO] Variant not ready or sold out status (422), retrying...", retries);
          return new Promise(function (resolve) {
            setTimeout(function () {
              resolve(addToCartWithRetry(variantId, props, retries - 1));
            }, 2000); // Wait 2 seconds for Shopify to index the CONTINUE inventory policy
          });
        }
        return res.json().then(function (e) { throw new Error(e.description || e.message || "Cart error"); });
      });
    }

    var variantSelections = this._getVariantSelections();
    var currentConfigStr = JSON.stringify(variantSelections);
    var variantPromise;

    // Helper to generate the variant on-demand
    function generateOnDemand() {
      return fetch(host() + "/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: self.config.id || self.config.productId || self.container.dataset.productId,
          selections: self._getCheckoutSelections(),
          precomputedPrice: self.displayPrice || self.price,
          shop: self.container.dataset.shopDomain || (w.Shopify && w.Shopify.shop)
        })
      })
        .then(function (r) {
          if (!r.ok) {
            return r.text().then(function (text) {
              try {
                var errJson = JSON.parse(text);
                var msg = errJson.error || errJson.message || "Server error " + r.status;
                throw new Error(msg + " (Debug: " + text + ")");
              } catch (_) {
                throw new Error("Server error " + r.status + " (HTML: " + text.slice(0, 150).replace(/</g, "&lt;").replace(/>/g, "&gt;") + "...)");
              }
            });
          }
          return r.json();
        })
        .then(function (data) {
          if (!data.variantId) throw new Error(data.error || "No variant returned");
          return data;
        });
    }

    // Retries variant creation itself (not just the final /cart/add call) a
    // couple of times with a short delay before giving up. The external
    // configurator backend can be slow/transiently flaky, and without this
    // a single hiccup here surfaced as a hard "variant creation failed"
    // error even though a retry a moment later would have succeeded.
    function generateOnDemandWithRetry(retries) {
      return generateOnDemand().catch(function (err) {
        if (retries > 0) {
          console.warn("[DPO] Variant generation failed, retrying...", retries, err);
          return new Promise(function (resolve) {
            setTimeout(function () {
              resolve(generateOnDemandWithRetry(retries - 1));
            }, 1500);
          });
        }
        throw err;
      });
    }

    // Friendly, on-brand messages shown (cycling) on the button while the
    // variant is being created — this step can take several seconds against
    // the external pricing/configurator backend, so this keeps the wait
    // feeling alive instead of a single static "Loading..." line.
    var stopCraftingCycle = startAtcMessageCycle(btn, [
      "Crafting your ring...",
      "Setting the details...",
      "Finalizing your design...",
      "Almost there..."
    ]);

    // Fast path: if the precomputed promise exists and matches current selections, use it!
    if (this.currentVariantPromise && this.currentSelectionsConfig === currentConfigStr) {
      variantPromise = this.currentVariantPromise.then(function (data) {
        if (!data) {
          console.warn("[DPO] Precomputed variant was null, falling back to on-demand generation...");
          return generateOnDemandWithRetry(2);
        }
        return data;
      });
    } else {
      // Fallback path: generate on demand if script just loaded or precompute didn't fire
      variantPromise = generateOnDemandWithRetry(2);
    }

    var cartProcessPromise = variantPromise
      .then(function (data) {
        stopCraftingCycle();
        var props = getProps();
        setAtcBtnState(btn, "Adding to your cart...", true);
        return addToCartWithRetry(data.variantId, props, 3);
      })
      .then(function (res) {
        return res.json();
      })
      .then(function (response) {
        console.log("[DPO Cart response]", response);
        // Always show the discount/sale price (displayPrice), not the full price
        setAtcBtnState(btn, "Add to Cart - " + fmt(self._lastBtnPrice || self.displayPrice), false);
        // NOTE: Do NOT publish cartUpdate event — it triggers CartItems.onCartUpdate()
        // which fetches main-cart-items (only on /cart page) causing "error updating cart" on product pages.
        var cd = d.querySelector('cart-drawer');
        var cn = d.querySelector('cart-notification');
        var cartDrawer = (cd && typeof cd.renderContents === 'function') ? cd : (cn && typeof cn.renderContents === 'function') ? cn : null;
        if (cartDrawer) {
          cartDrawer.classList.remove('is-empty');
          // If response includes section HTML, renderContents will update & open the drawer.
          // Fallback: if sections are missing (should not happen now), just open the drawer directly.
          if (response && response.sections) {
            cartDrawer.renderContents(response);
          } else {
            // Manually open the drawer so user sees the cart
            if (typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            }
          }
        } else {
          w.location.href = (w.Shopify && w.Shopify.routes ? w.Shopify.routes.root : "/") + "cart";
        }
      })
      .catch(function (err) {
        stopCraftingCycle();
        console.error("[DPO] Cart error:", err);
        var existingBox = d.getElementById("dpo-mini-cart");
        if (existingBox) {
          var m = existingBox.querySelector('.dpo-mc-modal');
          if (m) m.innerHTML = '<div style="padding:40px 20px;text-align:center;color:#d9534f;font-family:sans-serif;"><h3>Cart Error</h3><p>' + err.message + '</p><button onclick="document.getElementById(\'dpo-mini-cart\').remove()" style="padding:10px 20px;margin-top:20px;cursor:pointer;background:#eee;border:1px solid #ddd;border-radius:4px;">Close</button></div>';
        } else {
          if (errEl) { errEl.textContent = "Error: " + err.message; errEl.style.display = "block"; }
        }
        setAtcBtnState(btn, "Add to Cart - " + fmt(self._lastBtnPrice || self.displayPrice), false);
        throw err;
      });
  };

  Configurator.prototype._showOptimisticCart = function (cartPromise) {
    var self = this;
    var existing = d.getElementById("dpo-mini-cart");
    if (existing) existing.remove();

    // Premium Overlay (Soft blur)
    var overlay = el("div", "dpo-mc-overlay", { id: "dpo-mini-cart" });
    overlay.style.cssText = "position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.4);backdrop-filter:blur(4px);z-index:999999;display:flex;align-items:center;justify-content:center;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;opacity:0;transition:opacity 0.4s ease;";

    // Minimalist Modal
    var modal = el("div", "dpo-mc-modal");
    modal.style.cssText = "background:#fff;width:95%;max-width:440px;border-radius:12px;box-shadow:0 20px 40px rgba(0,0,0,0.15);overflow:hidden;transform:scale(0.95) translateY(10px);transition:transform 0.4s cubic-bezier(0.165, 0.84, 0.44, 1), opacity 0.4s ease;opacity:0;padding:0;";

    // Header
    var header = el("div"); header.style.cssText = "padding:24px 32px 16px;display:flex;justify-content:space-between;align-items:center;";
    var title = el("h3"); title.style.cssText = "margin:0;font-size:20px;color:#111;font-weight:600;letter-spacing:-0.01em;"; title.textContent = "Added to Cart";
    var closeBtn = el("button"); closeBtn.style.cssText = "background:none;border:none;font-size:20px;cursor:pointer;color:#888;padding:0;line-height:1;transition:color 0.2s;"; closeBtn.innerHTML = "&times;";
    closeBtn.onmouseover = function () { this.style.color = "#111"; };
    closeBtn.onmouseout = function () { this.style.color = "#888"; };
    closeBtn.onclick = function () { overlay.style.opacity = "0"; setTimeout(function () { overlay.remove(); window.location.reload(); }, 400); };
    header.appendChild(title); header.appendChild(closeBtn);
    modal.appendChild(header);

    // Item Detail Section
    var itemsWrap = el("div"); itemsWrap.style.cssText = "padding:16px 32px 24px;";
    var itemRow = el("div"); itemRow.style.cssText = "display:flex;align-items:flex-start;";

    var imgWrap = el("div"); imgWrap.style.cssText = "width:80px;height:80px;border-radius:8px;margin-right:20px;display:flex;align-items:center;justify-content:center;background:#f9f9f9;overflow:hidden;border:1px solid #f0f0f0;flex-shrink:0;";
    var pageImg = d.querySelector(".product__media img, .product-single__photo img, .product-image img, img.product-featured-img");
    if (pageImg) {
      var thumb = el("img"); thumb.src = pageImg.src; thumb.style.cssText = "max-width:100%;max-height:100%;object-fit:cover;";
      imgWrap.appendChild(thumb);
    }

    var detailsWrap = el("div"); detailsWrap.style.flex = "1";
    var prodTitle = el("div"); prodTitle.style.cssText = "color:#111;font-size:15px;font-weight:500;margin-bottom:6px;line-height:1.4;";
    var h1 = d.querySelector("h1.product-single__title, h1.product__title, h1");
    prodTitle.textContent = h1 ? h1.textContent.trim() : "Custom Configuration";

    var propsStr = "";
    var visIds = self._visibleIds();
    (self.sections || []).forEach(function (s) {
      if (s.fields) s.fields.forEach(function (f) {
        if (visIds.indexOf(f.id) !== -1 && !f.hideSkuInCart) {
          var sel = self.selections[f.id];
          if (sel) propsStr += '<span style="color:#777;">' + f.title + ':</span> ' + (Array.isArray(sel) ? sel.join(", ") : String(sel)) + "<br>";
        }
      });
    });

    var propsEl = el("div"); propsEl.style.cssText = "font-size:13px;color:#444;line-height:1.6;"; propsEl.innerHTML = propsStr;
    detailsWrap.appendChild(prodTitle); detailsWrap.appendChild(propsEl);

    itemRow.appendChild(imgWrap); itemRow.appendChild(detailsWrap);
    itemsWrap.appendChild(itemRow); modal.appendChild(itemsWrap);

    // Summary
    var summaryWrap = el("div"); summaryWrap.style.cssText = "padding:24px 32px;background:#fcfcfc;border-top:1px solid #f0f0f0;";
    var tRow = el("div"); tRow.style.cssText = "display:flex;justify-content:space-between;font-size:15px;color:#111;margin-bottom:4px;";
    var priceHtml = self.compareAtPrice
      ? '<span style="font-weight:400;color:#666;">Total</span><span style="font-weight:600;"><span style="text-decoration:line-through;color:#999;font-weight:400;margin-right:6px;">' + fmt(self.compareAtPrice) + '</span>' + fmt(self.displayPrice || self.price) + '</span>'
      : '<span style="font-weight:400;color:#666;">Total</span><span style="font-weight:600;">' + fmt(self.displayPrice || self.price) + '</span>';
    tRow.innerHTML = priceHtml;
    summaryWrap.appendChild(tRow);
    modal.appendChild(summaryWrap);

    // Actions
    var actWrap = el("div"); actWrap.style.cssText = "padding:24px 32px 32px;display:flex;gap:12px;background:#fcfcfc;";
    var viewBtn = el("button"); viewBtn.style.cssText = "flex:1;padding:14px;background:#fff;border:1px solid #e0e0e0;border-radius:6px;color:#111;cursor:pointer;font-size:14px;font-weight:500;transition:all 0.2s;";
    viewBtn.textContent = "View Cart";
    viewBtn.onmouseover = function () { this.style.borderColor = "#111"; };
    viewBtn.onmouseout = function () { this.style.borderColor = "#e0e0e0"; };

    var chkBtn = el("button"); chkBtn.style.cssText = "flex:1;padding:14px;background:#111;border:none;border-radius:6px;color:#fff;cursor:pointer;font-size:14px;font-weight:500;display:flex;align-items:center;justify-content:center;transition:background 0.2s;";
    chkBtn.innerHTML = "Checkout <span class='dpo-spin' style='display:inline-block;margin-left:10px;width:14px;height:14px;border:2px solid rgba(255,255,255,0.2);border-top-color:#fff;border-radius:50%;animation:dpospin 1s linear infinite;'></span>";
    chkBtn.onmouseover = function () { this.style.background = "#333"; };
    chkBtn.onmouseout = function () { this.style.background = "#111"; };

    var styleNode = d.createElement("style"); styleNode.textContent = "@keyframes dpospin { to { transform: rotate(360deg); } }"; d.head.appendChild(styleNode);

    var isReady = false;
    cartPromise.then(function () { isReady = true; chkBtn.querySelector('.dpo-spin').style.display = "none"; }).catch(function () { });

    var root = w.Shopify && w.Shopify.routes ? w.Shopify.routes.root : "/";
    viewBtn.onclick = function () {
      if (isReady) w.location.href = root + "cart";
      else { chkBtn.querySelector('.dpo-spin').style.display = "inline-block"; cartPromise.then(function () { w.location.href = root + "cart"; }); }
    };
    chkBtn.onclick = function () {
      if (!isReady) { chkBtn.querySelector('.dpo-spin').style.display = "inline-block"; cartPromise.then(function () { w.location.href = root + "checkout"; }); }
      else w.location.href = root + "checkout";
    };

    actWrap.appendChild(viewBtn); actWrap.appendChild(chkBtn); modal.appendChild(actWrap);
    overlay.appendChild(modal); d.body.appendChild(overlay);

    setTimeout(function () {
      overlay.style.opacity = "1";
      modal.style.opacity = "1";
      modal.style.transform = "scale(1) translateY(0)";
    }, 50);
  };


  // ── Init ───────────────────────────────────────────────────────────────────

  function initAll() {
    // Guard: if this script loads twice (App Embed AND direct theme injection both active),
    // only ever initialize a single configurator — whichever container appears first in DOM.
    if (window._dpoInitDone) return;
    window._dpoInitDone = true;

    var containers = d.querySelectorAll("[data-configurator]");
    if (!containers.length) return;

    // Only boot the first container; hide/remove any extras so the page doesn't duplicate.
    for (var i = 0; i < containers.length; i++) {
      if (i === 0) {
        containers[i].dataset.dpoInit = '1';
        new Configurator(containers[i]);
      } else {
        // Extra container (duplicate injection) — remove it silently.
        containers[i].parentNode && containers[i].parentNode.removeChild(containers[i]);
      }
    }
  }

  if (d.readyState === "loading") d.addEventListener("DOMContentLoaded", initAll);
  else initAll();

})(window, document);
